// ─── PERMANENT PROJECT RULES - DO NOT CHANGE THESE ───────────────────────────
// 1. Template is generated dynamically: GET /api/employee/get-employee-template
// 2. Download filename: dAppsEmployeeUploadTemplate.xlsx
// 3. FaDownload icon and its import are REMOVED from this project - never add back
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { dnAlert } from "./utils/dNews_Alert";
import { FaFilePdf, FaFileExcel, FaUserPlus, FaEdit, FaTrash, FaTimes, FaUsers } from "react-icons/fa";
import Select, { components } from "react-select";
import ExcelJS from "exceljs";
import JSZip from "jszip";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "./utils/api";
import ScrollSelect from "./ScrollSelect";
import { addEagleWatermark } from "./utils/pdfWatermark";
import DOLLUZ_CORP from "./assets/img/DOLLUZ_CORP.png";
import logo_eagle from "./assets/img/logo_eagle.png";
// NOTE: Template is now generated dynamically from the server via GET /api/employee/get-employee-template
import "./dSpr.css";
import "./Employee_Center.css";

// ─── constants ────────────────────────────────────────────────────────────────

const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
const ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];
const BLOOD_GROUPS = ["A+", "A−", "B+", "B−", "AB+", "AB−", "O+", "O−", "Unknown"];
const GENDER_OPTIONS = ["Male", "Female", "Non-binary", "Prefer not to say", "Other"];

const SAME_AS_OPTIONS = [
    { value: "current", label: "Same as current address" },
    { value: "permanent", label: "Same as permanent address" },
];

const EMPTY_EC = {
    first_name: "", last_name: "", relation: "", phone: "", email: "",
    addr_same_as: "",
    addr_line1: "", addr_line2: "", addr_city: "", addr_state: "", addr_pincode: "",
};

const EMPTY_FORM = {
    emp_first_name: "", emp_last_name: "", dob: "", blood_group: "",
    gender: "", nationality: "", personal_email: "",
    emp_mail_id: "", account_pass_text: "",
    emp_mobile_no: "", emp_alternate_mobile_no: "",
    emp_department: "", career_level: "", job_position: "", emp_type: "",
    bank_name: "", bank_account_number: "", ifsc_code: "",
    aadhar_number: "", pan_number: "", passport_number: "",
    passport_expiry: "", passport_country: "",
    // reporting_manager is owned by the Manager Allocation page; never written from this modal.
    ctc: "", joining_date: "", active: true,
    internal_notes: "",
    emp_location: "", emp_access_level: "", emp_id: "",
    curr_addr_line1: "", curr_addr_line2: "", curr_addr_city: "", curr_addr_state: "", curr_addr_pincode: "",
    perm_addr_same_as_curr: false,
    perm_addr_line1: "", perm_addr_line2: "", perm_addr_city: "", perm_addr_state: "", perm_addr_pincode: "",
    ec1: { ...EMPTY_EC },
    ec2: { ...EMPTY_EC },
    ec3: { ...EMPTY_EC },
};

// ─── helpers ──────────────────────────────────────────────────────────────────

// Format the joined reporting-manager fields the backend now exposes
// (reporting_manager_first_name / _last_name) as "First Last (DZIND###)".
// Falls back to the raw emp_id if the JOIN row is missing for any reason,
// "-" when the employee has no reporting manager at all.
const formatReportingManager = (emp) => {
    if (!emp || !emp.reporting_manager) return "-";
    const first = emp.reporting_manager_first_name || "";
    const last = emp.reporting_manager_last_name || "";
    const fullName = `${first} ${last}`.trim();
    return fullName ? `${fullName} (${emp.reporting_manager})` : emp.reporting_manager;
};

const bloodGroupOptions = BLOOD_GROUPS.map(bg => ({ value: bg, label: bg }));
const genderOptions = GENDER_OPTIONS.map(g => ({ value: g, label: g }));
const locationOptions = LOCATIONS.map(l => ({ value: l, label: l }));
const accessLevelOptions = ACCESS_LEVELS.map(a => ({ value: a, label: a }));

// Turn a bulk-upload failure into per-Excel-row detail.
//
// The backend already attributes every error it raises to a row and returns
// them as `rows`, so that is always preferred. The value-matching fallback
// below only runs for anything that arrives unattributed (an older backend, a
// raw MySQL error surfacing a duplicate value, a proxy rewriting the body) -
// it re-finds the offending sheet row by scanning the payload for the emp_id,
// mobile, or config value quoted in the message.
const resolveServerErrorRows = (error, serverError, finalData) => {
    if (Array.isArray(error?.rows) && error.rows.length > 0) {
        return error.rows.map(r => ({
            excelRow: r.excelRow ?? "-",
            empId: r.empId || "-",
            name: r.name || "-",
            email: r.email || "-",
            errors: Array.isArray(r.errors) && r.errors.length ? r.errors : [serverError],
        }));
    }

    const describe = (row, message) => ({
        excelRow: row.__excelRow ?? "-",
        empId: (row["Employee ID"] || "").toString().trim() || "-",
        name: [row["First Name"], row["Last Name"]].filter(Boolean).join(" ") || "-",
        email: row["Email"] || "-",
        errors: [message],
    });

    // Values quoted after a colon, e.g. "Duplicate Mobile inside Excel: 9999, 8888"
    const quoted = (serverError.split(":").slice(1).join(":") || "")
        .split(/[,;]/)
        .map(v => v.trim().replace(/\s*\(owned by .*\)$/i, ""))
        .filter(Boolean);
    if (quoted.length === 0) return [];

    const matched = [];
    for (const row of finalData) {
        const empId = (row["Employee ID"] || "").toString().trim();
        const mobile = (row["Mobile"] || "").toString().trim();
        const configVals = ["Department", "Job Position", "Career Level", "Employee Type"]
            .map(k => (row[k] || "").toString().trim())
            .filter(Boolean);
        const name = [row["First Name"], row["Last Name"]].filter(Boolean).join(" ");

        const hit = quoted.some(v =>
            (empId && v === empId) ||
            (mobile && v === mobile) ||
            (name && v === name) ||
            configVals.includes(v)
        );
        if (hit) matched.push(describe(row, serverError));
    }
    return matched;
};

// ─── sub-component: bulk-upload error report ──────────────────────────────────

function BulkErrorModal({ report, onClose }) {
    if (!report) return null;
    return (
        <div className="bulk-error-overlay" onClick={e => e.stopPropagation()}>
            <div className="bulk-error-modal">
                <div className="bulk-error-header">
                    <h3>{report.title}</h3>
                    <button type="button" className="bulk-error-close" onClick={onClose} aria-label="Close">
                        <FaTimes />
                    </button>
                </div>

                <p className="bulk-error-note">{report.note}</p>

                <div className="bulk-error-table-wrap">
                    <table className="bulk-error-table">
                        <thead>
                            <tr>
                                <th className="bec-row">Excel Row</th>
                                <th>Employee ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Errors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.rows.map((r, i) => (
                                <tr key={`${r.excelRow}-${i}`}>
                                    <td className="bec-row">{r.excelRow}</td>
                                    <td className="bec-id">{r.empId}</td>
                                    <td className="bec-name">{r.name}</td>
                                    <td className="bec-email">{r.email}</td>
                                    <td>
                                        {r.errors.map((e, j) => (
                                            <span key={j} className="bec-badge">{e}</span>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="bulk-error-footer">
                    <button type="button" className="bulk-error-btn" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

// ─── sub-component: Emergency Contact block ───────────────────────────────────

function EmergencyContactBlock({ label, ecKey, formData, setFormData, required = false, errors = {} }) {
    const ec = formData[ecKey];
    const req = required ? <span className="required">*</span> : null;

    const handleEcChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [ecKey]: { ...prev[ecKey], [field]: value }
        }));
    };

    const handleSameAsChange = (selected) => {
        const val = selected ? selected.value : "";
        if (val === "current") {
            setFormData(prev => ({
                ...prev,
                [ecKey]: {
                    ...prev[ecKey],
                    addr_same_as: "current",
                    addr_line1: prev.curr_addr_line1,
                    addr_line2: prev.curr_addr_line2,
                    addr_city: prev.curr_addr_city,
                    addr_state: prev.curr_addr_state,
                    addr_pincode: prev.curr_addr_pincode,
                }
            }));
        } else if (val === "permanent") {
            setFormData(prev => ({
                ...prev,
                [ecKey]: {
                    ...prev[ecKey],
                    addr_same_as: "permanent",
                    addr_line1: prev.perm_addr_line1,
                    addr_line2: prev.perm_addr_line2,
                    addr_city: prev.perm_addr_city,
                    addr_state: prev.perm_addr_state,
                    addr_pincode: prev.perm_addr_pincode,
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [ecKey]: {
                    ...prev[ecKey],
                    addr_same_as: "",
                    addr_line1: "", addr_line2: "",
                    addr_city: "", addr_state: "", addr_pincode: "",
                }
            }));
        }
    };

    const addrReadOnly = !!ec.addr_same_as;

    return (
        <div className="ec-block">
            <div className="section-title" style={{ marginTop: 0 }}>{label}</div>
            <div className="ec-grid">
                <div className="field-group">
                    <label>First Name {req}</label>
                    <input type="text" placeholder="First name" value={ec.first_name}
                        className={errors[`${ecKey}_first_name`] ? "input-error" : ""}
                        onChange={e => handleEcChange("first_name", e.target.value)} />
                </div>
                <div className="field-group">
                    <label>Last Name {req}</label>
                    <input type="text" placeholder="Last name" value={ec.last_name}
                        className={errors[`${ecKey}_last_name`] ? "input-error" : ""}
                        onChange={e => handleEcChange("last_name", e.target.value)} />
                </div>
                <div className="field-group">
                    <label>Relation {req}</label>
                    <input type="text" placeholder="e.g. Spouse, Parent" value={ec.relation}
                        className={errors[`${ecKey}_relation`] ? "input-error" : ""}
                        onChange={e => handleEcChange("relation", e.target.value)} />
                </div>
                <div className="field-group">
                    <label>Phone {req}</label>
                    <input type="text" placeholder="10-digit phone" maxLength={10} value={ec.phone}
                        className={errors[`${ecKey}_phone`] ? "input-error" : ""}
                        onChange={e => handleEcChange("phone", e.target.value.replace(/\D/g, ""))} />
                </div>
                <div className="field-group">
                    <label>Email</label>
                    <input type="email" placeholder="email@example.com" value={ec.email}
                        className={errors[`${ecKey}_email`] ? "input-error" : ""}
                        onChange={e => handleEcChange("email", e.target.value)} />
                </div>
            </div>

            <div style={{ margin: "10px 0 8px" }}>
                <label style={{ fontWeight: 500, marginRight: 10 }}>Address</label>
                <Select
                    options={SAME_AS_OPTIONS}
                    isClearable
                    placeholder="- or enter manually below -"
                    value={SAME_AS_OPTIONS.find(o => o.value === ec.addr_same_as) || null}
                    onChange={handleSameAsChange}
                    styles={{ container: p => ({ ...p, maxWidth: 280, display: "inline-block" }) }}
                />
            </div>

            <div className="ec-grid">
                <div className="field-group" style={{ flex: "1 1 100%" }}>
                    <label>Address Line 1</label>
                    <input type="text" placeholder="Street / area" value={ec.addr_line1} readOnly={addrReadOnly}
                        style={addrReadOnly ? { background: "#f5f5f5" } : {}}
                        onChange={e => !addrReadOnly && handleEcChange("addr_line1", e.target.value)} />
                </div>
                <div className="field-group" style={{ flex: "1 1 100%" }}>
                    <label>Address Line 2</label>
                    <input type="text" placeholder="Landmark / flat No." value={ec.addr_line2} readOnly={addrReadOnly}
                        style={addrReadOnly ? { background: "#f5f5f5" } : {}}
                        onChange={e => !addrReadOnly && handleEcChange("addr_line2", e.target.value)} />
                </div>
                <div className="field-group">
                    <label>City</label>
                    <input type="text" placeholder="City" value={ec.addr_city} readOnly={addrReadOnly}
                        style={addrReadOnly ? { background: "#f5f5f5" } : {}}
                        onChange={e => !addrReadOnly && handleEcChange("addr_city", e.target.value)} />
                </div>
                <div className="field-group">
                    <label>State</label>
                    <input type="text" placeholder="State" value={ec.addr_state} readOnly={addrReadOnly}
                        style={addrReadOnly ? { background: "#f5f5f5" } : {}}
                        onChange={e => !addrReadOnly && handleEcChange("addr_state", e.target.value)} />
                </div>
                <div className="field-group">
                    <label>Pincode</label>
                    <input type="text" placeholder="6-digit pincode" maxLength={6} value={ec.addr_pincode}
                        readOnly={addrReadOnly}
                        className={errors[`${ecKey}_pincode`] ? "input-error" : ""}
                        style={addrReadOnly ? { background: "#f5f5f5" } : {}}
                        onChange={e => !addrReadOnly && handleEcChange("addr_pincode", e.target.value.replace(/\D/g, ""))} />
                </div>
            </div>
        </div>
    );
}

// ─── SectionHeader - defined outside component to avoid recreation on every render ──
// Supports multiple sections open at once. `openSections` is a Set of open section IDs.
function SectionHeader({ id, title, openSections, toggleSection }) {
    const isOpen = openSections.has(id);
    return (
        <div className="accordion-header" onClick={() => toggleSection(id)}>
            <span>{title}</span>
            <span>{isOpen ? "▲" : "▼"}</span>
        </div>
    );
}

// ─── main component ───────────────────────────────────────────────────────────

function Employee_Center({ navSize }) {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [editingEmp, setEditingEmp] = useState(null);

    const [showBulkModal, setShowBulkModal] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    // { title, note, rows: [{ excelRow, empId, name, email, errors[] }] }
    const [bulkErrors, setBulkErrors] = useState(null);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [formData, setFormData] = useState({ ...EMPTY_FORM });
    const [options, setOptions] = useState({ department: [], job: [], level: [], type: [] });

    // ── Resume upload state ──
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeUploading, setResumeUploading] = useState(false);
    const [resumePendingDelete, setResumePendingDelete] = useState(false); // true = delete on Save

    // ── Accordion state ── multiple sections may be open simultaneously
    const [openSections, setOpenSections] = useState(() => new Set(["basic"]));
    const toggleSection = (id) => {
        setOpenSections(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // ── Global busy/loader state for import/export/download actions ──
    // Holds a label string while an action runs, null when idle. Used both to
    // render a blocking overlay AND to guard against double-clicks.
    const [actionLoading, setActionLoading] = useState(null);
    // Runs `fn` behind the loader overlay; ignores re-entry while one is already running.
    const runWithLoader = async (label, fn) => {
        if (actionLoading) return;              // already busy → ignore the extra click
        setActionLoading(label);
        // let the overlay paint before any heavy (possibly synchronous) work runs
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        try {
            await fn();
        } catch (err) {
            console.error(`${label} failed:`, err);
        } finally {
            setActionLoading(null);
        }
    };

    // ── Dynamic EC count (1-3) ──
    const [ecCount, setEcCount] = useState(1);

    useEffect(() => {
        fetchConfigData();
        fetchloginedEmployees();
        fetchEmployees();
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: 'GET', credentials: 'include' });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") { navigate("/login"); return; }
            // Role redirect removed - ProtectedRoute (App.js) gates via matrix.
        } catch (err) { console.error("Error fetching employees:", err); }
        finally { setLoading(false); }
    };

    const fetchEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/all`);
            const data = await res.json();
            setEmployees(data);
        } catch (err) { console.error("Error fetching employees:", err); }
        finally { setLoading(false); }
    };

    const fetchConfigData = async () => {
        try {
            const [deptRes, jobRes, levelRes, typeRes] = await Promise.all([
                apiFetch("/api/Employee_configuration/get-department_config"),
                apiFetch("/api/Employee_configuration/get-job_position_config"),
                apiFetch("/api/Employee_configuration/get-career_level_config"),
                apiFetch("/api/Employee_configuration/get-employee_type_config"),
            ]);
            const [deptData, jobData, levelData, typeData] = await Promise.all([
                deptRes.json(), jobRes.json(), levelRes.json(), typeRes.json()
            ]);
            setOptions({
                department: deptData.map(d => ({ value: d.department_id, label: d.department_name })),
                job: jobData.map(j => ({ value: j.job_id, label: j.job_name })),
                level: levelData.map(l => ({ value: l.level_id, label: l.level_name })),
                type: typeData.map(t => ({ value: t.type_id, label: t.type_name })),
            });
        } catch (error) {
            console.error("❌ Error fetching config:", error);
            dnAlert.error("Error", "Failed to load dropdown data");
        }
    };

    // ── Pagination & filter state ──
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 75, 100];

    const filterOptions = [
        { value: "emp_first_name", label: "First name" },
        { value: "emp_last_name", label: "Last name" },
        { value: "emp_mail_id", label: "Email" },
        { value: "emp_department", label: "Department" },
        { value: "emp_type", label: "Employee type" },
        { value: "bank_name", label: "Bank name" },
        { value: "bank_account_number", label: "Bank Acc.No" },
        { value: "ifsc_code", label: "IFSC code" },
        { value: "aadhar_number", label: "Aadhar No" },
        { value: "pan_number", label: "PAN No" },
        { value: "passport_number", label: "Passport No" },
        { value: "emp_location", label: "Location" },
        { value: "emp_access_level", label: "Access level" },
        { value: "emp_id", label: "Employee ID" },
        { value: "created_time", label: "Created date" },
    ];

    const filterableColumns = filterOptions.map(f => f.value);
    const [selectedFilter, setSelectedFilter] = useState("emp_first_name");
    const [filters, setFilters] = useState(filterableColumns.reduce((acc, col) => ({ ...acc, [col]: [] }), {}));

    // ── Sort ──
    const SORT_OPTIONS = [
        { value: "emp_id_asc", label: "Employee Id A → Z" },
        { value: "emp_id_desc", label: "Employee Id Z → A" },
        { value: "name_asc", label: "Name A → Z" },
        { value: "name_desc", label: "Name Z → A" },
        { value: "doj_desc", label: "DOJ (newest first)" },
        { value: "doj_asc", label: "DOJ (oldest first)" },
        { value: "created_desc", label: "Recently added" },
        { value: "created_asc", label: "First added" },
    ];
    const [sortOrder, setSortOrder] = useState("emp_id_asc");
    const [showSortDropdown, setShowSortDropdown] = useState(false);

    // Close sort dropdown when clicking outside
    useEffect(() => {
        if (!showSortDropdown) return;
        const handler = (e) => {
            if (!e.target.closest("[data-sort-dropdown]")) setShowSortDropdown(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [showSortDropdown]);

    const sortEmployees = (list) => {
        const sorted = [...list];
        switch (sortOrder) {
            case "emp_id_asc": return sorted.sort((a, b) => (a.emp_id || "").localeCompare(b.emp_id || "", undefined, { numeric: true }));
            case "emp_id_desc": return sorted.sort((a, b) => (b.emp_id || "").localeCompare(a.emp_id || "", undefined, { numeric: true }));
            case "name_asc": return sorted.sort((a, b) => (a.emp_first_name || "").localeCompare(b.emp_first_name || ""));
            case "name_desc": return sorted.sort((a, b) => (b.emp_first_name || "").localeCompare(a.emp_first_name || ""));
            case "doj_desc": return sorted.sort((a, b) => new Date(b.joining_date || 0) - new Date(a.joining_date || 0));
            case "doj_asc": return sorted.sort((a, b) => new Date(a.joining_date || 0) - new Date(b.joining_date || 0));
            case "created_desc": return sorted.sort((a, b) => new Date(b.created_time || 0) - new Date(a.created_time || 0));
            case "created_asc": return sorted.sort((a, b) => new Date(a.created_time || 0) - new Date(b.created_time || 0));
            default: return sorted;
        }
    };

    const CheckboxOption = (props) => (
        <components.Option {...props}>
            <input type="checkbox" checked={props.isSelected} onChange={() => null}
                style={{ marginRight: 8, width: 14, height: 14, transform: "scale(1.2)", cursor: "pointer" }} />
            <label style={{ cursor: "pointer" }}>{props.label}</label>
        </components.Option>
    );

    const getOptions = (key) => {
        const unique = [...new Set(employees.map(t => t[key]))].filter(Boolean);
        return unique.map(u => ({ value: u, label: u }));
    };

    // ── Input change handler ──
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const numericOnly = v => v.replace(/\D/g, "");
        const upperAlnum = v => v.toUpperCase().replace(/[^A-Z0-9]/g, "");

        const transforms = {
            emp_mobile_no: v => numericOnly(v),
            emp_alternate_mobile_no: v => numericOnly(v),
            bank_account_number: v => numericOnly(v),
            ifsc_code: v => upperAlnum(v).slice(0, 11),
            aadhar_number: v => numericOnly(v).slice(0, 12),
            pan_number: v => upperAlnum(v).slice(0, 10),
            passport_number: v => upperAlnum(v).slice(0, 9),
            curr_addr_pincode: v => numericOnly(v).slice(0, 6),
            perm_addr_pincode: v => numericOnly(v).slice(0, 6),
        };

        const transformed = transforms[name] ? transforms[name](value) : value;

        setFormData(prev => {
            const next = { ...prev, [name]: transformed };

            // ── Live sync: curr_addr → perm_addr (when same-as is ticked) ──
            const currToPermMap = {
                curr_addr_line1: "perm_addr_line1",
                curr_addr_line2: "perm_addr_line2",
                curr_addr_city: "perm_addr_city",
                curr_addr_state: "perm_addr_state",
                curr_addr_pincode: "perm_addr_pincode",
            };
            if (prev.perm_addr_same_as_curr && currToPermMap[name]) {
                next[currToPermMap[name]] = transformed;
            }

            // ── Live sync: curr/perm addr → EC blocks ──
            const isCurrField = name in currToPermMap;
            const isPermField = ["perm_addr_line1", "perm_addr_line2", "perm_addr_city", "perm_addr_state", "perm_addr_pincode"].includes(name);

            if (isCurrField || isPermField) {
                // map field name to EC sub-key
                const ecFieldMap = {
                    curr_addr_line1: "addr_line1", perm_addr_line1: "addr_line1",
                    curr_addr_line2: "addr_line2", perm_addr_line2: "addr_line2",
                    curr_addr_city: "addr_city", perm_addr_city: "addr_city",
                    curr_addr_state: "addr_state", perm_addr_state: "addr_state",
                    curr_addr_pincode: "addr_pincode", perm_addr_pincode: "addr_pincode",
                };
                const ecSubKey = ecFieldMap[name];
                const srcType = isCurrField ? "current" : "permanent";

                // Also sync perm fields when curr changes and perm_addr_same_as_curr is on
                ["ec1", "ec2", "ec3"].forEach(ecKey => {
                    const ec = next[ecKey];
                    if (!ec) return;
                    if (ec.addr_same_as === srcType) {
                        next[ecKey] = { ...ec, [ecSubKey]: transformed };
                    }
                    // If perm was updated via curr-sync, also update ECs linked to "permanent"
                    if (isCurrField && prev.perm_addr_same_as_curr && ec.addr_same_as === "permanent") {
                        next[ecKey] = { ...next[ecKey], [ecSubKey]: transformed };
                    }
                });
            }

            return next;
        });
    };

    // ── Perm address same-as-curr toggle ──
    const handlePermSameToggle = (e) => {
        const checked = e.target.checked;
        setFormData(prev => {
            const next = {
                ...prev,
                perm_addr_same_as_curr: checked,
                perm_addr_line1: checked ? prev.curr_addr_line1 : "",
                perm_addr_line2: checked ? prev.curr_addr_line2 : "",
                perm_addr_city: checked ? prev.curr_addr_city : "",
                perm_addr_state: checked ? prev.curr_addr_state : "",
                perm_addr_pincode: checked ? prev.curr_addr_pincode : "",
            };

            // If any EC is linked to "permanent", update them too
            ["ec1", "ec2", "ec3"].forEach(ecKey => {
                const ec = prev[ecKey];
                if (ec?.addr_same_as === "permanent") {
                    next[ecKey] = {
                        ...ec,
                        addr_line1: next.perm_addr_line1,
                        addr_line2: next.perm_addr_line2,
                        addr_city: next.perm_addr_city,
                        addr_state: next.perm_addr_state,
                        addr_pincode: next.perm_addr_pincode,
                    };
                }
            });

            return next;
        });
    };

    // ── Remove last EC and clear its data ──
    const handleRemoveLastEc = () => {
        const key = `ec${ecCount}`;
        setFormData(prev => ({ ...prev, [key]: { ...EMPTY_EC } }));
        setEcCount(c => c - 1);
    };

    // ── Validation - collect ALL errors, show once ──
    const handleSave = async () => {
        const errorList = [];
        const newErrors = {};

        const fail = (field, msg) => {
            newErrors[field] = true;
            errorList.push(msg);
        };

        // Basic info
        if (!formData.emp_first_name) fail("emp_first_name", "First Name is required");
        if (!formData.emp_last_name) fail("emp_last_name", "Last Name is required");

        // DOB
        if (!formData.dob) {
            fail("dob", "Date of Birth is required");
        } else {
            const selDate = new Date(formData.dob);
            const today = new Date();
            if (selDate >= today) {
                fail("dob", "DOB cannot be today or a future date");
            } else {
                const age = today.getFullYear() - selDate.getFullYear();
                const hasBD = today.getMonth() > selDate.getMonth() ||
                    (today.getMonth() === selDate.getMonth() && today.getDate() >= selDate.getDate());
                if ((hasBD ? age : age - 1) < 15) fail("dob", "Employee must be at least 15 years old");
            }
        }

        // Blood Group - optional (no required check)

        // Email - required
        if (!formData.emp_mail_id) {
            fail("emp_mail_id", "Email is required");
        } else if (!formData.emp_mail_id.endsWith("@dolluzcorp.com")) {
            fail("emp_mail_id", "Email must end with @dolluzcorp.com");
        }

        // Mobile - required
        if (!formData.emp_mobile_no) {
            fail("emp_mobile_no", "Mobile is required");
        } else if (formData.emp_mobile_no.length !== 10) {
            fail("emp_mobile_no", "Mobile must be 10 digits");
        }

        // Alternate mobile - OPTIONAL; validate format & uniqueness only if provided
        if (formData.emp_alternate_mobile_no) {
            if (formData.emp_alternate_mobile_no.length !== 10) {
                fail("emp_alternate_mobile_no", "Alternate Mobile must be 10 digits");
            } else if (formData.emp_alternate_mobile_no === formData.emp_mobile_no) {
                fail("emp_alternate_mobile_no", "Alternate Mobile cannot be same as Primary Mobile");
            }
        }

        if (!formData.emp_department) fail("emp_department", "Department is required");
        if (!formData.career_level) fail("career_level", "Career Level is required");
        if (!formData.job_position) fail("job_position", "Job Position is required");
        if (!formData.emp_type) fail("emp_type", "Employee Type is required");
        if (!formData.emp_location) fail("emp_location", "Location is required");

        // Employee ID format
        const empfieldId = formData.emp_id?.trim();
        if (empfieldId && !/^DZIND\d{3}$/.test(empfieldId)) {
            fail("emp_id", "Employee ID must be DZIND followed by exactly 3 digits (e.g. DZIND001)");
        }

        if (!formData.emp_access_level) fail("emp_access_level", "Access Level is required");
        if (!formData.account_pass_text) fail("account_pass_text", "Account Password is required");

        // Bank details - REQUIRED
        if (!formData.bank_name) {
            fail("bank_name", "Bank Name is required");
        } else if (/^\d+$/.test(formData.bank_name)) {
            fail("bank_name", "Bank Name cannot be only numbers");
        }
        if (!formData.bank_account_number) fail("bank_account_number", "Bank Account Number is required");

        if (!formData.ifsc_code) {
            fail("ifsc_code", "IFSC Code is required");
        } else if (!/^[A-Za-z]{4}\d{7}$/.test(formData.ifsc_code.trim())) {
            fail("ifsc_code", "Invalid IFSC Code format (e.g. SBIN0001234)");
        }

        // KYC - REQUIRED
        if (!formData.aadhar_number) {
            fail("aadhar_number", "Aadhar Number is required");
        } else if (!/^\d{12}$/.test(formData.aadhar_number.trim())) {
            fail("aadhar_number", "Aadhar must be exactly 12 digits");
        }

        if (!formData.pan_number) {
            fail("pan_number", "PAN Number is required");
        } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number.trim())) {
            fail("pan_number", "Invalid PAN format (e.g. ABCDE1234F - 5 letters, 4 digits, 1 letter)");
        }

        // Passport Number - OPTIONAL (not every employee holds a passport).
        // No format check either; passport formats vary by issuing country. The
        // input transform already caps it at 9 uppercase alphanumerics, and the
        // varchar(20) length guard below still applies.

        // Passport expiry - optional; must be a future date if provided
        if (formData.passport_expiry) {
            const expiry = new Date(formData.passport_expiry);
            expiry.setHours(0, 0, 0, 0);
            const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
            if (expiry <= todayMid) fail("passport_expiry", "Passport Expiry must be a future date");
        }

        // Personal email - optional; basic format check if provided, and must differ from Official Mail ID
        if (formData.personal_email) {
            const pe = formData.personal_email.trim().toLowerCase();
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pe)) {
                fail("personal_email", "Personal Email format is invalid");
            } else if (formData.emp_mail_id && pe === formData.emp_mail_id.trim().toLowerCase()) {
                fail("personal_email", "Personal Email cannot be the same as Official Mail ID");
            }
        }

        // CTC - OPTIONAL; if provided must be a positive number AND fit decimal(10,2)
        if (formData.ctc !== "" && formData.ctc !== null && formData.ctc !== undefined) {
            const ctcNum = Number(formData.ctc);
            if (isNaN(ctcNum) || ctcNum <= 0) {
                fail("ctc", "CTC must be a positive number");
            } else if (ctcNum > 99999999.99) {
                // DB column is decimal(10,2) → 8 digits before decimal + 2 after
                fail("ctc", "CTC cannot exceed 99,999,999.99");
            }
        }

        // DOJ (Date of Joining) - REQUIRED
        if (!formData.joining_date) fail("joining_date", "DOJ is required");

        // Reporting Manager is NOT validated here - it's assigned via the
        // dedicated Manager Allocation page (governed by access-level hierarchy).

        // ── DB varchar length caps - guard against SQL truncation/errors ──
        // Each entry: [formData key, human label, max chars from DB schema]
        const lengthChecks = [
            ["emp_first_name", "First Name", 100],
            ["emp_last_name", "Last Name", 100],
            ["nationality", "Nationality", 60],
            ["emp_mail_id", "Official Mail ID", 150],
            ["personal_email", "Personal Email", 180],
            ["account_pass_text", "Account Password", 255],
            ["bank_name", "Bank Name", 100],
            ["bank_account_number", "Bank Account Number", 50],
            ["passport_number", "Passport Number", 20],
            ["passport_country", "Passport Country", 100],
            ["curr_addr_line1", "Current Address Line 1", 200],
            ["curr_addr_line2", "Current Address Line 2", 200],
            ["curr_addr_city", "Current City", 100],
            ["curr_addr_state", "Current State", 100],
            ["perm_addr_line1", "Permanent Address Line 1", 200],
            ["perm_addr_line2", "Permanent Address Line 2", 200],
            ["perm_addr_city", "Permanent City", 100],
            ["perm_addr_state", "Permanent State", 100],
            // reporting_manager intentionally NOT length-checked here - owned by Manager Allocation page.
        ];
        lengthChecks.forEach(([key, label, max]) => {
            const v = formData[key];
            if (v && String(v).length > max) fail(key, `${label} cannot exceed ${max} characters`);
        });
        // EC fields - same shape repeated for ec1/ec2/ec3
        const ecLengthChecks = [
            ["first_name", "First Name", 60],
            ["last_name", "Last Name", 60],
            ["relation", "Relation", 60],
            ["email", "Email", 180],
            ["addr_line1", "Address Line 1", 200],
            ["addr_line2", "Address Line 2", 200],
            ["addr_city", "City", 100],
            ["addr_state", "State", 100],
        ];
        [1, 2, 3].forEach(n => {
            const ec = formData[`ec${n}`];
            if (!ec) return;
            ecLengthChecks.forEach(([k, label, max]) => {
                if (ec[k] && String(ec[k]).length > max) {
                    fail(`ec${n}_${k}`, `Emergency Contact ${n} ${label} cannot exceed ${max} characters`);
                }
            });
        });

        // ── EC1 - at least one emergency contact mandatory (core 4 fields) ──
        // EC2 and EC3 stay fully optional (only validate format when filled)
        const ec1 = formData.ec1 || {};
        if (!ec1.first_name?.trim()) fail("ec1_first_name", "Emergency Contact 1: First Name is required");
        if (!ec1.last_name?.trim()) fail("ec1_last_name", "Emergency Contact 1: Last Name is required");
        if (!ec1.relation?.trim()) fail("ec1_relation", "Emergency Contact 1: Relation is required");
        if (!ec1.phone?.trim()) {
            fail("ec1_phone", "Emergency Contact 1: Phone is required");
        } else if (!/^\d{10}$/.test(ec1.phone.trim())) {
            fail("ec1_phone", "Emergency Contact 1: Phone must be 10 digits");
        }
        // EC1 Email format check if provided
        if (ec1.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ec1.email.trim())) {
            fail("ec1_email", "Emergency Contact 1: Email format is invalid");
        }
        // EC1 Pincode format check if provided
        if (ec1.addr_pincode && !/^\d{6}$/.test(ec1.addr_pincode.trim())) {
            fail("ec1_pincode", "Emergency Contact 1: Pincode must be 6 digits");
        }

        // EC2 / EC3 - fully optional, but format-check phone/email/pincode when provided
        ["ec2", "ec3"].forEach((key, i) => {
            const ec = formData[key] || {};
            const label = `Emergency Contact ${i + 2}`;
            if (ec.phone && !/^\d{10}$/.test(ec.phone.trim())) fail(`${key}_phone`, `${label}: Phone must be 10 digits`);
            if (ec.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ec.email.trim())) fail(`${key}_email`, `${label}: Email format is invalid`);
            if (ec.addr_pincode && !/^\d{6}$/.test(ec.addr_pincode.trim())) fail(`${key}_pincode`, `${label}: Pincode must be 6 digits`);
        });

        // Apply all errors to state
        setErrors(newErrors);

        // If any errors, show all at once and stop
        if (errorList.length > 0) {
            dnAlert.error(
                "Please fix the following errors",
                errorList.map((m, i) => `${i + 1}. ${m}`).join("\n")
            );
            return;
        }

        try {
            const url = editingEmp ? `/api/employee/update/${editingEmp.emp_id}` : `/api/employee/create`;
            const method = editingEmp ? "PUT" : "POST";

            const response = await apiFetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
                credentials: "include",
            });

            const data = typeof response.json === "function" ? await response.json() : response;
            if (data.error) throw new Error(data.error);

            const savedEmpId = data.emp_id || editingEmp?.emp_id;

            // Execute staged resume delete (only now, after save confirmed)
            if (resumePendingDelete && savedEmpId) {
                await apiFetch(`/api/employee/delete-resume/${savedEmpId}`, { method: "DELETE", credentials: "include" });
            }

            // Upload new resume if a file was selected
            if (resumeFile && savedEmpId) {
                setResumeUploading(true);
                const formPayload = new FormData();
                formPayload.append("resume", resumeFile);
                await apiFetch(`/api/employee/upload-resume/${savedEmpId}`, { method: "POST", body: formPayload, credentials: "include" });
                setResumeUploading(false);
            }

            dnAlert.success("Success", editingEmp ? "Employee updated" : "Employee added");
            setShowModal(false);
            setFormData({ ...EMPTY_FORM });
            setEditingEmp(null);
            setResumeFile(null);
            setResumePendingDelete(false);
            setEcCount(1);
            fetchEmployees();
        } catch (err) {
            dnAlert.error("Error", err.message);
        }
    };

    const handleDelete = async (id) => {
        const ok = await dnAlert.confirm({
            title: "Are you sure?",
            message: "This will mark the employee as deleted.",
            danger: true,
            confirmLabel: "Yes, delete it!",
        });
        if (!ok) return;
        try {
            await apiFetch(`/api/employee/delete/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include" });
            dnAlert.success("Deleted!", "Employee has been deleted.");
            fetchEmployees();
        } catch {
            dnAlert.error("Error", "Failed to delete employee");
        }
    };

    const getMatchedValue = (list, value) => {
        if (!list || !value) return "";
        const found = list.find(opt => opt.value === value || opt.label === value);
        return found ? found.value : "";
    };

    const openEditModal = (emp) => {
        setEditingEmp(emp);
        setFormData({
            ...emp,
            dob: emp.dob ? emp.dob.split("T")[0] : "",
            gender: emp.gender || "",
            nationality: emp.nationality || "",
            personal_email: emp.personal_email || "",
            passport_expiry: emp.passport_expiry ? emp.passport_expiry.split("T")[0] : "",
            passport_country: emp.passport_country || "",
            ctc: emp.ctc !== null && emp.ctc !== undefined ? emp.ctc : "",
            joining_date: emp.joining_date ? emp.joining_date.split("T")[0] : "",
            // reporting_manager intentionally NOT pulled into formData - owned by Manager Allocation.
            active: emp.active !== undefined ? !!emp.active : true,
            internal_notes: emp.internal_notes || "",
            emp_department: getMatchedValue(options.department, emp.emp_department),
            emp_type: getMatchedValue(options.type, emp.emp_type),
            job_position: getMatchedValue(options.job, emp.job_position),
            career_level: getMatchedValue(options.level, emp.career_level),
            perm_addr_same_as_curr: !!emp.perm_addr_same_as_curr,
            ec1: {
                first_name: emp.ec1_first_name || "", last_name: emp.ec1_last_name || "",
                relation: emp.ec1_relation || "", phone: emp.ec1_phone || "", email: emp.ec1_email || "",
                addr_same_as: emp.ec1_addr_same_as || "",
                addr_line1: emp.ec1_addr_line1 || "", addr_line2: emp.ec1_addr_line2 || "",
                addr_city: emp.ec1_addr_city || "", addr_state: emp.ec1_addr_state || "", addr_pincode: emp.ec1_addr_pincode || "",
            },
            ec2: {
                first_name: emp.ec2_first_name || "", last_name: emp.ec2_last_name || "",
                relation: emp.ec2_relation || "", phone: emp.ec2_phone || "", email: emp.ec2_email || "",
                addr_same_as: emp.ec2_addr_same_as || "",
                addr_line1: emp.ec2_addr_line1 || "", addr_line2: emp.ec2_addr_line2 || "",
                addr_city: emp.ec2_addr_city || "", addr_state: emp.ec2_addr_state || "", addr_pincode: emp.ec2_addr_pincode || "",
            },
            ec3: {
                first_name: emp.ec3_first_name || "", last_name: emp.ec3_last_name || "",
                relation: emp.ec3_relation || "", phone: emp.ec3_phone || "", email: emp.ec3_email || "",
                addr_same_as: emp.ec3_addr_same_as || "",
                addr_line1: emp.ec3_addr_line1 || "", addr_line2: emp.ec3_addr_line2 || "",
                addr_city: emp.ec3_addr_city || "", addr_state: emp.ec3_addr_state || "", addr_pincode: emp.ec3_addr_pincode || "",
            },
        });
        // Set ecCount based on any filled field across all EC slots (not just first_name)
        const hasEc = (prefix) => ['first_name', 'last_name', 'phone', 'email', 'relation']
            .some(f => emp[`${prefix}_${f}`]);
        const filledCount = ['ec1', 'ec2', 'ec3'].filter(hasEc).length;
        setEcCount(Math.max(1, filledCount));
        setResumeFile(null);
        setResumePendingDelete(false);
        setOpenSections(new Set(["basic"]));
        setErrors({});
        setShowModal(true);
    };

    // ── Filtering ──
    const filteredEmployees = employees.filter(emp =>
        filterableColumns.every(col => {
            if (col === "created_time") {
                const empDate = new Date(emp.created_time);
                if (fromDate && !toDate) return empDate >= new Date(fromDate);
                if (!fromDate && toDate) return empDate <= new Date(toDate);
                if (fromDate && toDate) return empDate >= new Date(fromDate) && empDate <= new Date(toDate);
                return true;
            }
            if (!filters[col] || filters[col].length === 0) return true;
            return filters[col].some(f => f.value === emp[col]);
        })
    );

    const sortedEmployees = sortEmployees(filteredEmployees);
    const totalPages = Math.ceil(sortedEmployees.length / rowsPerPage);
    const paginatedEmployees = sortedEmployees.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    // ── Export ──
    const exportEmployeesToPDF = (employees) => {
        if (!employees?.length) { dnAlert.warning("Nothing to export", "No employees available to export."); return; }
        const doc = new jsPDF("landscape");
        const pageWidth = doc.internal.pageSize.getWidth();

        // palette
        const ORANGE = [232, 82, 10];
        const DARK = [31, 41, 55];
        const TEXT = [40, 40, 40];

        // helpers
        const fullName = emp => `${emp.emp_first_name || ""} ${emp.emp_last_name || ""}`.trim() || "-";
        const fmtDate = iso => (iso ? iso.split("T")[0] : "-");
        const fmtAddr = (l1, l2, city, state, pin) =>
            [l1, l2, city, state, pin].filter(v => v && String(v).trim() !== "").join(", ") || "-";
        const fmtEC = (fn, ln, rel, phone) => {
            const name = [fn, ln].filter(Boolean).join(" ");
            if (!name && !phone) return "-";
            let s = name || "";
            if (rel) s += ` (${rel})`;
            if (phone) s += `${s ? " - " : ""}${phone}`;
            return s || "-";
        };

        const genTime = new Date().toLocaleString();

        // Orange title banner - drawn on every page via didDrawPage
        const drawBanner = () => {
            doc.setFillColor(...ORANGE);
            doc.rect(0, 0, pageWidth, 22, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold"); doc.setFontSize(15);
            doc.text("Employee Database Export", 10, 11);
            doc.setFont("helvetica", "normal"); doc.setFontSize(9);
            doc.text(`All Employees  •  ${employees.length} employees  •  Generated: ${genTime}`, 10, 18);
        };

        // Dark "Section N - Title" bar
        const drawSectionBar = (title, y) => {
            doc.setFillColor(...DARK);
            doc.rect(6, y, pageWidth - 12, 9, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFont("helvetica", "bold"); doc.setFontSize(10);
            doc.text(title, 11, y + 6.2);
            return y + 11;
        };

        // Section definitions - together cover almost every employee column
        const sections = [
            {
                title: "Section 1 - Employment Details",
                head: ["Employee Id", "Name", "Designation", "Department", "Official Email", "Phone", "DOJ", "Status", "CTC (Rs.)", "Reporting Manager"],
                statusCol: 7,
                row: emp => [
                    emp.emp_id, fullName(emp), emp.job_position || "-", emp.emp_department || "-",
                    emp.emp_mail_id || "-", emp.emp_mobile_no || "-", fmtDate(emp.joining_date),
                    emp.active ? "Active" : "Inactive",
                    emp.ctc ? Number(emp.ctc).toLocaleString("en-IN") : "0.00",
                    // Manager is read-only on this PDF; format from the JOIN columns
                    formatReportingManager(emp) === "-" ? "-" : formatReportingManager(emp),
                ],
            },
            {
                title: "Section 2 - Personal Details",
                head: ["Employee Id", "Name", "Gender", "Date of Birth", "Blood Group", "Nationality", "Personal Email", "Career Level", "Employee Type", "Location"],
                row: emp => [
                    emp.emp_id, fullName(emp), emp.gender || "-", fmtDate(emp.dob), emp.blood_group || "-",
                    emp.nationality || "-", emp.personal_email || "-", emp.career_level || "-",
                    emp.emp_type || "-", emp.emp_location || "-",
                ],
            },
            {
                title: "Section 3 - KYC & Bank Details",
                head: ["Employee Id", "Name", "Aadhaar", "PAN", "Passport", "Bank Name", "Bank Account", "IFSC"],
                row: emp => [
                    emp.emp_id, fullName(emp), emp.aadhar_number || "-", emp.pan_number || "-",
                    emp.passport_number || "-", emp.bank_name || "-", emp.bank_account_number || "-", emp.ifsc_code || "-",
                ],
            },
            {
                title: "Section 4 - Address & Emergency Contact",
                head: ["Employee Id", "Name", "Current Address", "Permanent Address", "Emergency Contact"],
                row: emp => [
                    emp.emp_id, fullName(emp),
                    fmtAddr(emp.curr_addr_line1, emp.curr_addr_line2, emp.curr_addr_city, emp.curr_addr_state, emp.curr_addr_pincode),
                    emp.perm_addr_same_as_curr
                        ? "Same as Current"
                        : fmtAddr(emp.perm_addr_line1, emp.perm_addr_line2, emp.perm_addr_city, emp.perm_addr_state, emp.perm_addr_pincode),
                    fmtEC(emp.ec1_first_name, emp.ec1_last_name, emp.ec1_relation, emp.ec1_phone),
                ],
                columnStyles: { 2: { cellWidth: 90 }, 3: { cellWidth: 90 } },
            },
        ];

        sections.forEach((sec, idx) => {
            if (idx > 0) doc.addPage();
            const startY = drawSectionBar(sec.title, 28);
            autoTable(doc, {
                head: [sec.head],
                body: employees.map(sec.row),
                startY,
                margin: { top: 28, left: 6, right: 6 },
                theme: "grid",
                styles: { fontSize: 7, cellPadding: 2, overflow: "linebreak", font: "helvetica", textColor: TEXT, valign: "middle" },
                headStyles: { fillColor: ORANGE, textColor: 255, fontStyle: "bold", fontSize: 7.5 },
                alternateRowStyles: { fillColor: [252, 246, 240] },
                columnStyles: sec.columnStyles || {},
                didDrawPage: drawBanner,
                willDrawCell: (data) => {
                    // colour the Status cell like a badge
                    if (sec.statusCol !== undefined && data.section === "body" && data.column.index === sec.statusCol) {
                        if (data.cell.raw === "Active") { data.cell.styles.fillColor = [212, 237, 218]; data.cell.styles.textColor = [21, 87, 36]; }
                        else if (data.cell.raw === "Inactive") { data.cell.styles.fillColor = [248, 215, 218]; data.cell.styles.textColor = [114, 28, 36]; }
                    }
                },
            });
        });

        const dateStr = new Date().toISOString().split("T")[0];
        addEagleWatermark(doc);
        doc.save(`Employees_All_Employees_${dateStr}.pdf`);
    };

    // Export uses the SAME column structure as the bulk-import template so that
    // an exported file can be edited and re-uploaded to UPDATE those employees.
    const exportEmployeesToExcel = async (employees) => {
        if (!employees?.length) { dnAlert.warning("Nothing to export", "No employees available to export."); return; }

        // YYYY-MM-DD → DD-MM-YYYY (round-trip compatibility with the template header format)
        const fmtDmy = (iso) => {
            if (!iso) return "";
            const ymd = iso.includes("T") ? iso.split("T")[0] : iso;
            const parts = ymd.split("-");
            if (parts.length !== 3) return "";
            const [y, m, d] = parts;
            return `${d}-${m}-${y}`;
        };

        // Must mirror EMP_COLS in backend get-employee-template - SAME column order.
        // Follows the modal UI section flow:
        //   Basic Info → Address → Bank & KYC → EC1 → EC2 → EC3 → Notes
        // `key` = flat field on the /api/employee/all row; `ec` = { n, f } pulls ec{n}_{f}.
        const ecGet = (emp, n, field) => emp[`ec${n}_${field}`] ?? "";
        const COLS = [
            // Employee ID - always the first column
            { header: "Employee ID", key: "emp_id", width: 16 },
            // ══ Section 1: Basic Info ══
            { header: "First Name", key: "emp_first_name", width: 16, req: true },
            { header: "Last Name", key: "emp_last_name", width: 16, req: true },
            { header: "Email", key: "emp_mail_id", width: 30, req: true },
            { header: "Personal Email", key: "personal_email", width: 28 },
            { header: "Gender", key: "gender", width: 16, dropdown: true },
            { header: "Nationality", key: "nationality", width: 16 },
            { header: "Location", key: "emp_location", width: 16, req: true, dropdown: true },
            { header: "Mobile", key: "emp_mobile_no", width: 16, req: true },
            { header: "Alternate Mobile", key: "emp_alternate_mobile_no", width: 16 },
            { header: "DOB (DD-MM-YYYY)", key: "dob", width: 18, req: true, isDate: true },
            { header: "Blood Group", key: "blood_group", width: 14, dropdown: true },
            { header: "Department", key: "emp_department", width: 28, req: true, dropdown: true },
            { header: "Employee Type", key: "emp_type", width: 20, req: true, dropdown: true },
            { header: "Job Position", key: "job_position", width: 28, req: true, dropdown: true },
            { header: "Career Level", key: "career_level", width: 22, req: true, dropdown: true },
            { header: "CTC (Annual)", key: "ctc", width: 14, isNumber: true },
            { header: "DOJ (DD-MM-YYYY)", key: "joining_date", width: 22, req: true, isDate: true },
            // Reporting Manager is display-only in the export (read-only, formatted "Name (DZIND###)").
            // Not part of the import template; the Manager Allocation page owns this field.
            { header: "Reporting Manager", key: "reporting_manager", width: 28, isManager: true },
            { header: "Active", key: "active", width: 10, isActive: true, dropdown: true },
            { header: "Access Level", key: "emp_access_level", width: 16, req: true, dropdown: true },
            { header: "Account Password", key: "account_pass_text", width: 20, req: true },

            // ══ Section 2: Address ══
            { header: "Current Address Line 1", key: "curr_addr_line1", width: 26 },
            { header: "Current Address Line 2", key: "curr_addr_line2", width: 26 },
            { header: "Current City", key: "curr_addr_city", width: 14 },
            { header: "Current State", key: "curr_addr_state", width: 14 },
            { header: "Current Pincode", key: "curr_addr_pincode", width: 12 },
            { header: "Permanent Address Same As Current", key: "perm_addr_same_as_curr", width: 14, isYesNo: true, dropdown: true },
            { header: "Permanent Address Line 1", key: "perm_addr_line1", width: 26 },
            { header: "Permanent Address Line 2", key: "perm_addr_line2", width: 26 },
            { header: "Permanent City", key: "perm_addr_city", width: 14 },
            { header: "Permanent State", key: "perm_addr_state", width: 14 },
            { header: "Permanent Pincode", key: "perm_addr_pincode", width: 12 },

            // ══ Section 3: Bank & KYC ══
            { header: "Bank Name", key: "bank_name", width: 20, req: true },
            { header: "Bank Account Number", key: "bank_account_number", width: 22, req: true },
            { header: "IFSC Code", key: "ifsc_code", width: 14, req: true },
            { header: "Aadhar Number", key: "aadhar_number", width: 18, req: true },
            { header: "PAN Number", key: "pan_number", width: 14, req: true },
            { header: "Passport Number", key: "passport_number", width: 18 },
            { header: "Passport Expiry (DD-MM-YYYY)", key: "passport_expiry", width: 22, isDate: true },
            { header: "Passport Country", key: "passport_country", width: 18 },

            // ══ Section 4: Emergency Contacts ══
            // EC1 - mandatory core 4; rest optional
            { header: "EC1 First Name", key: null, ec: { n: 1, f: "first_name" }, width: 16, req: true },
            { header: "EC1 Last Name", key: null, ec: { n: 1, f: "last_name" }, width: 16, req: true },
            { header: "EC1 Relation", key: null, ec: { n: 1, f: "relation" }, width: 14, req: true },
            { header: "EC1 Phone", key: null, ec: { n: 1, f: "phone" }, width: 14, req: true },
            { header: "EC1 Email", key: null, ec: { n: 1, f: "email" }, width: 28 },
            { header: "EC1 Address Same As", key: null, ec: { n: 1, f: "addr_same_as" }, width: 14, dropdown: true },
            { header: "EC1 Address Line 1", key: null, ec: { n: 1, f: "addr_line1" }, width: 26 },
            { header: "EC1 Address Line 2", key: null, ec: { n: 1, f: "addr_line2" }, width: 26 },
            { header: "EC1 City", key: null, ec: { n: 1, f: "addr_city" }, width: 14 },
            { header: "EC1 State", key: null, ec: { n: 1, f: "addr_state" }, width: 14 },
            { header: "EC1 Pincode", key: null, ec: { n: 1, f: "addr_pincode" }, width: 12 },

            // EC2 - fully optional
            { header: "EC2 First Name", key: null, ec: { n: 2, f: "first_name" }, width: 16 },
            { header: "EC2 Last Name", key: null, ec: { n: 2, f: "last_name" }, width: 16 },
            { header: "EC2 Relation", key: null, ec: { n: 2, f: "relation" }, width: 14 },
            { header: "EC2 Phone", key: null, ec: { n: 2, f: "phone" }, width: 14 },
            { header: "EC2 Email", key: null, ec: { n: 2, f: "email" }, width: 28 },
            { header: "EC2 Address Same As", key: null, ec: { n: 2, f: "addr_same_as" }, width: 14, dropdown: true },
            { header: "EC2 Address Line 1", key: null, ec: { n: 2, f: "addr_line1" }, width: 26 },
            { header: "EC2 Address Line 2", key: null, ec: { n: 2, f: "addr_line2" }, width: 26 },
            { header: "EC2 City", key: null, ec: { n: 2, f: "addr_city" }, width: 14 },
            { header: "EC2 State", key: null, ec: { n: 2, f: "addr_state" }, width: 14 },
            { header: "EC2 Pincode", key: null, ec: { n: 2, f: "addr_pincode" }, width: 12 },

            // EC3 - fully optional
            { header: "EC3 First Name", key: null, ec: { n: 3, f: "first_name" }, width: 16 },
            { header: "EC3 Last Name", key: null, ec: { n: 3, f: "last_name" }, width: 16 },
            { header: "EC3 Relation", key: null, ec: { n: 3, f: "relation" }, width: 14 },
            { header: "EC3 Phone", key: null, ec: { n: 3, f: "phone" }, width: 14 },
            { header: "EC3 Email", key: null, ec: { n: 3, f: "email" }, width: 28 },
            { header: "EC3 Address Same As", key: null, ec: { n: 3, f: "addr_same_as" }, width: 14, dropdown: true },
            { header: "EC3 Address Line 1", key: null, ec: { n: 3, f: "addr_line1" }, width: 26 },
            { header: "EC3 Address Line 2", key: null, ec: { n: 3, f: "addr_line2" }, width: 26 },
            { header: "EC3 City", key: null, ec: { n: 3, f: "addr_city" }, width: 14 },
            { header: "EC3 State", key: null, ec: { n: 3, f: "addr_state" }, width: 14 },
            { header: "EC3 Pincode", key: null, ec: { n: 3, f: "addr_pincode" }, width: 12 },

            // ══ Section 5: Notes ══
            { header: "Internal Notes", key: "internal_notes", width: 32 },
        ];

        const workbook = new ExcelJS.Workbook();
        const ws = workbook.addWorksheet("Employees");

        // Column widths
        COLS.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });

        // ── Rows 1-3: logo ─────────────────────────────────────────────
        try {
            const imgRes = await fetch(DOLLUZ_CORP);
            const imgBuffer = await (await imgRes.blob()).arrayBuffer();
            const logoId = workbook.addImage({ buffer: imgBuffer, extension: "png" });
            ws.addImage(logoId, { tl: { col: 0, row: 0 }, ext: { width: 220, height: 60 } });
            [1, 2, 3].forEach(r => { ws.getRow(r).height = 16; });
        } catch (logoErr) {
            console.warn("Logo embed failed in export:", logoErr);
        }

        // ── Row 4: column headers (orange = required, dark grey = optional) ──
        const hRow = ws.getRow(4);
        hRow.height = 36;
        COLS.forEach((col, i) => {
            const cell = hRow.getCell(i + 1);
            cell.value = col.header;
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: col.req ? "FFE8520A" : col.dropdown ? "FF1E3A5F" : "FF374151" } };
            cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
            cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
            cell.border = {
                top: { style: "thin", color: { argb: "FFD1D5DB" } },
                left: { style: "thin", color: { argb: "FFD1D5DB" } },
                bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
                right: { style: "thin", color: { argb: "FFD1D5DB" } },
            };
        });

        // ── Row 5+: data rows ──────────────────────────────────────────
        employees.forEach((emp, idx) => {
            const row = ws.getRow(5 + idx);
            row.height = 18;
            COLS.forEach((col, i) => {
                const cell = row.getCell(i + 1);
                let value = "";
                if (col.ec) {
                    // Emergency Contact field - read flat ec{n}_{field} from /api/employee/all row
                    const raw = ecGet(emp, col.ec.n, col.ec.f);
                    // Capitalize addr_same_as so "current" → "Current" for the dropdown match
                    if (col.ec.f === "addr_same_as" && raw) {
                        value = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
                    } else {
                        value = raw ?? "";
                    }
                } else if (col.isDate) {
                    value = fmtDmy(emp[col.key]);
                } else if (col.isNumber) {
                    const v = emp[col.key];
                    value = (v !== null && v !== undefined && v !== "") ? Number(v) : "";
                } else if (col.isActive) {
                    value = emp[col.key] ? "Yes" : "No";
                } else if (col.isYesNo) {
                    value = emp[col.key] ? "Yes" : "No";
                } else if (col.isManager) {
                    // Display-only: "First Last (DZIND###)" sourced from the backend JOIN.
                    const formatted = formatReportingManager(emp);
                    value = formatted === "-" ? "" : formatted;
                } else {
                    value = emp[col.key] ?? "";
                }
                cell.value = value;
                cell.font = { size: 9, color: { argb: "FF374151" } };
                cell.alignment = { vertical: "middle" };
                cell.border = {
                    top: { style: "thin", color: { argb: "FFE5E7EB" } },
                    left: { style: "thin", color: { argb: "FFE5E7EB" } },
                    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
                    right: { style: "thin", color: { argb: "FFE5E7EB" } },
                };
            });
        });

        // Freeze header
        ws.views = [{ state: "frozen", ySplit: 4 }];

        // ── Dropdown_values sheet - mirrors backend template exactly ──────────
        // Columns: A=Dept, B=Job, C=Career, D=EmpType, E=Blood, F=Gender, G=YesNo, H=Location, I=AccessLevel, J=AddrSameAs
        const DV_BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];
        const DV_GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say", "Other"];
        const DV_YES_NO = ["Yes", "No"];                  // Used by Active + Permanent Address Same As Current
        const DV_LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
        const DV_ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];
        const DV_ADDR_SAME_AS = ["Current", "Permanent"]; // EC*_Address_Same_As

        // Fetch live DB-backed values from the already-loaded options state
        const DV_DEPARTMENTS = options.department.map(o => o.label);
        const DV_JOB_POSITIONS = options.job.map(o => o.label);
        const DV_CAREER_LEVELS = options.level.map(o => o.label);
        const DV_EMP_TYPES = options.type.map(o => o.label);

        const DV_COLS_EXPORT = [
            { title: "Departments", values: DV_DEPARTMENTS, width: 46, colLetter: "A" },
            { title: "Job Positions", values: DV_JOB_POSITIONS, width: 36, colLetter: "B" },
            { title: "Career Levels", values: DV_CAREER_LEVELS, width: 28, colLetter: "C" },
            { title: "Employee Types", values: DV_EMP_TYPES, width: 22, colLetter: "D" },
            { title: "Blood Groups", values: DV_BLOOD_GROUPS, width: 16, colLetter: "E" },
            { title: "Genders", values: DV_GENDERS, width: 22, colLetter: "F" },
            { title: "Yes / No", values: DV_YES_NO, width: 10, colLetter: "G" },
            { title: "Locations", values: DV_LOCATIONS, width: 20, colLetter: "H" },
            { title: "Access Levels", values: DV_ACCESS_LEVELS, width: 20, colLetter: "I" },
            { title: "Address Same As", values: DV_ADDR_SAME_AS, width: 18, colLetter: "J" },
        ];

        const wsDV = workbook.addWorksheet("Dropdown_values");
        wsDV.state = "visible";
        DV_COLS_EXPORT.forEach((c, i) => { wsDV.getColumn(i + 1).width = c.width; });

        // Header row
        const dvHdrRow = wsDV.getRow(1);
        dvHdrRow.height = 26;
        DV_COLS_EXPORT.forEach(({ title }, i) => {
            const cell = dvHdrRow.getCell(i + 1);
            cell.value = title;
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A5F" } };
            cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = { top: { style: "thin", color: { argb: "FFD1D5DB" } }, left: { style: "thin", color: { argb: "FFD1D5DB" } }, bottom: { style: "thin", color: { argb: "FFD1D5DB" } }, right: { style: "thin", color: { argb: "FFD1D5DB" } } };
        });

        // Data rows
        const dvMaxRows = Math.max(...DV_COLS_EXPORT.map(c => c.values.length));
        for (let r = 0; r < dvMaxRows; r++) {
            const dvRow = wsDV.getRow(r + 2);
            dvRow.height = 18;
            DV_COLS_EXPORT.forEach(({ values }, i) => {
                if (r >= values.length) return;
                const cell = dvRow.getCell(i + 1);
                cell.value = values[r];
                cell.font = { size: 9, color: { argb: "FF374151" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: r % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB" } };
                cell.alignment = { vertical: "middle" };
                cell.border = { top: { style: "thin", color: { argb: "FFE5E7EB" } }, left: { style: "thin", color: { argb: "FFE5E7EB" } }, bottom: { style: "thin", color: { argb: "FFE5E7EB" } }, right: { style: "thin", color: { argb: "FFE5E7EB" } } };
            });
        }

        // Warning note
        const warnRowNum = dvMaxRows + 3;
        const warnCell = wsDV.getCell(warnRowNum, 1);
        warnCell.value = "⚠  Do not edit this sheet - values are referenced by dropdowns in the Employees sheet.";
        warnCell.font = { italic: true, size: 9, color: { argb: "FF92400E" } };
        warnCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF7ED" } };
        warnCell.alignment = { vertical: "middle" };
        wsDV.mergeCells(warnRowNum, 1, warnRowNum, DV_COLS_EXPORT.length);

        // ── Wire data validations onto the Employees sheet (rows 5-500) ───────
        // col index (1-based) in COLS → dropdown source info
        const colLetter = n => n <= 26
            ? String.fromCharCode(64 + n)
            : String.fromCharCode(64 + Math.floor((n - 1) / 26)) + String.fromCharCode(65 + ((n - 1) % 26));

        const DROPDOWN_MAP = {
            "Department": { dvCol: "A", count: DV_DEPARTMENTS.length },
            "Job Position": { dvCol: "B", count: DV_JOB_POSITIONS.length },
            "Career Level": { dvCol: "C", count: DV_CAREER_LEVELS.length },
            "Employee Type": { dvCol: "D", count: DV_EMP_TYPES.length },
            "Blood Group": { dvCol: "E", count: DV_BLOOD_GROUPS.length },
            "Gender": { dvCol: "F", count: DV_GENDERS.length },
            "Active": { dvCol: "G", count: DV_YES_NO.length },
            "Location": { dvCol: "H", count: DV_LOCATIONS.length },
            "Access Level": { dvCol: "I", count: DV_ACCESS_LEVELS.length },
            "Permanent Address Same As Current": { dvCol: "G", count: DV_YES_NO.length },
            "EC1 Address Same As": { dvCol: "J", count: DV_ADDR_SAME_AS.length },
            "EC2 Address Same As": { dvCol: "J", count: DV_ADDR_SAME_AS.length },
            "EC3 Address Same As": { dvCol: "J", count: DV_ADDR_SAME_AS.length },
        };

        COLS.forEach((col, i) => {
            const dv = DROPDOWN_MAP[col.header];
            if (!dv) return;
            const cl = colLetter(i + 1);
            const dvFormula = `'Dropdown_values'!$${dv.dvCol}$2:$${dv.dvCol}$${dv.count + 1}`;
            for (let row = 5; row <= 500; row++) {
                ws.getCell(`${cl}${row}`).dataValidation = {
                    type: "list", allowBlank: true, formulae: [dvFormula],
                    showDropDown: false, showErrorMessage: true,
                    errorTitle: "Invalid value",
                    error: "Please select from the dropdown list.",
                };
            }
        });

        const blob = new Blob([await workbook.xlsx.writeBuffer()], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "Employees_Report.xlsx";
        link.click();
    };

    // ── Bulk upload helpers ──
    const getCellString = (value) => {
        if (!value) return "";
        if (value instanceof Date) { const y = value.getUTCFullYear(); const m = String(value.getUTCMonth() + 1).padStart(2, "0"); const d = String(value.getUTCDate()).padStart(2, "0"); return `${y}-${m}-${d}`; }
        if (typeof value === "string") return value.trim();
        if (typeof value === "number") return value.toString().trim();
        if (value.text) return value.text.trim();
        if (value.hyperlink) return value.hyperlink.replace("mailto:", "").trim();
        return value.toString().trim();
    };

    // ── Flexible date parser - accepts 6 formats, returns {year,month,day} or null ──
    // Accepted: YYYY-MM-DD  YYYY/MM/DD  YYYY.MM.DD  DD/MM/YYYY  DD-MM-YYYY  DD.MM.YYYY
    const parseFlexDate = (raw) => {
        if (!raw) return null;
        const s = raw.toString().trim();
        // Serial number from Excel (days since 1900-01-01)
        if (/^\d{5}$/.test(s)) {
            const serial = parseInt(s, 10);
            const d = new Date(Date.UTC(1900, 0, serial - 1));
            return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
        }
        const SEP = "[-/.]";
        const patterns = [
            // YYYY-MM-DD  YYYY/MM/DD  YYYY.MM.DD
            { re: new RegExp(`^(\\d{4})${SEP}(\\d{1,2})${SEP}(\\d{1,2})$`), y: 1, m: 2, d: 3 },
            // DD-MM-YYYY  DD/MM/YYYY  DD.MM.YYYY
            { re: new RegExp(`^(\\d{1,2})${SEP}(\\d{1,2})${SEP}(\\d{4})$`), y: 3, m: 2, d: 1 },
        ];
        for (const { re, y, m, d } of patterns) {
            const match = s.match(re);
            if (match) {
                const year = +match[y], month = +match[m], day = +match[d];
                // Basic sanity: month 1-12, day 1-31
                if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                    return { year, month, day };
                }
            }
        }
        return null; // unrecognised format
    };

    const toIsoDate = ({ year, month, day }) =>
        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

    const handleBulkUpload = async () => {
        if (!excelFile) { dnAlert.warning("Missing file", "Please upload an Excel file!"); return; }
        if (actionLoading) return;                       // guard against double-click
        setActionLoading("Uploading & validating…");
        try {
            const workbook = new ExcelJS.Workbook();
            const reader = new FileReader();
            reader.onerror = () => setActionLoading(null);
            reader.onload = async (e) => {
              try {
                // ── Pre-strip drawings from the xlsx zip before ExcelJS parses it ──
                // ExcelJS crashes on twoCellAnchor images (logos), comments, and threadedComments
                // (e.g. "Cannot read properties of undefined (reading 'comments')"). We rewrite the
                // zip in-memory: strip all non-data parts and their relationships so ExcelJS never
                // encounters them. Row data is completely unaffected.
                let xlsxBuffer = e.target.result;
                try {
                    const zip = await JSZip.loadAsync(xlsxBuffer);

                    // Files to remove - drawings, media, charts, comments (legacy + threaded), persons
                    const toDelete = [];
                    zip.forEach((relPath) => {
                        if (
                            relPath.startsWith("xl/drawings/") ||
                            relPath.startsWith("xl/media/") ||
                            relPath.startsWith("xl/charts/") ||
                            relPath.startsWith("xl/comments") ||           // xl/comments1.xml, xl/comments2.xml, ...
                            relPath.startsWith("xl/threadedComments/") ||
                            relPath.startsWith("xl/persons/")
                        ) toDelete.push(relPath);
                    });
                    toDelete.forEach(p => zip.remove(p));

                    // Single regex matching all stripped target types in .rels files
                    const STRIP_RELS_RE = /<Relationship\s[^>]*Target=["'][^"']*(?:drawing|media|chart|comments|threadedComments?|persons?)[^"']*["'][^>]*\/?>/gi;

                    // Fix .rels files - strip matching Relationship entries; delete file if empty
                    const relFiles = Object.keys(zip.files).filter(f => f.endsWith(".rels"));
                    for (const relFile of relFiles) {
                        let content = await zip.files[relFile].async("string");
                        content = content.replace(STRIP_RELS_RE, "");
                        const hasRelationships = /<Relationship\s/i.test(content);
                        if (!hasRelationships) {
                            zip.remove(relFile);
                        } else {
                            zip.file(relFile, content);
                        }
                    }

                    // Strip references from worksheet XML itself
                    const sheetFiles = Object.keys(zip.files).filter(
                        f => f.startsWith("xl/worksheets/") && f.endsWith(".xml") && !f.includes("_rels")
                    );
                    for (const sheetFile of sheetFiles) {
                        let content = await zip.files[sheetFile].async("string");
                        content = content.replace(/<drawing\s[^>]*\/>/gi, "");
                        content = content.replace(/<picture\s[^>]*\/>/gi, "");
                        content = content.replace(/<legacyDrawing\s[^>]*\/>/gi, "");   // comment anchors
                        content = content.replace(/<legacyDrawingHF\s[^>]*\/>/gi, "");
                        zip.file(sheetFile, content);
                    }

                    // Strip [Content_Types].xml Override entries for removed parts
                    const ctFile = "[Content_Types].xml";
                    if (zip.files[ctFile]) {
                        let content = await zip.files[ctFile].async("string");
                        content = content.replace(
                            /<Override\s[^>]*PartName=["'][^"']*(?:comments|threadedComments|persons|drawing|chart|media)[^"']*["'][^>]*\/?>/gi,
                            ""
                        );
                        zip.file(ctFile, content);
                    }

                    // Strip references from workbook.xml.rels too
                    const wbRels = "xl/_rels/workbook.xml.rels";
                    if (zip.files[wbRels]) {
                        let content = await zip.files[wbRels].async("string");
                        content = content.replace(STRIP_RELS_RE, "");
                        zip.file(wbRels, content);
                    }

                    xlsxBuffer = await zip.generateAsync({ type: "arraybuffer" });
                } catch (stripErr) {
                    console.warn("Pre-strip failed, proceeding with original:", stripErr);
                }

                try {
                    await workbook.xlsx.load(xlsxBuffer, {
                        ignoreNodes: ['dataValidations', 'threadedComments', 'comments'],
                    });
                } catch (loadErr) {
                    console.error("ExcelJS load failed:", loadErr);
                    dnAlert.error(
                        "Could not read Excel file",
                        "The uploaded file could not be parsed. Re-save the file in Excel (File → Save As → .xlsx), or download a fresh template."
                    );
                    return;
                }
                const worksheet = workbook.getWorksheet(1) || workbook.getWorksheet("Employees");
                // parsedRows holds EVERY sheet row with its accumulated errors.
                // Splitting into finalData/errorData is deferred until after the
                // whole-sheet checks below, so a row can pick up both a per-row
                // error and a cross-row one in the same pass.
                let excelHeaders = []; const parsedRows = [];
                const finalData = []; const errorData = [];

                // Optional columns - anything not in this list is required for INSERT.
                // NOTE: Account Password and EC1 core (First Name, Last Name, Relation, Phone)
                //       are intentionally NOT in this list (explicitly checked below for INSERT only).
                const allowedEmpty = [
                    "Employee ID",
                    // Basic optionals
                    "Alternate Mobile", "Blood Group", "Gender", "Nationality", "Personal Email",
                    "Active", "Passport Number", "Passport Expiry (DD-MM-YYYY)", "Passport Country",
                    "CTC (Annual)",
                    // Reporting Manager has been removed from the template - kept here for back-compat
                    // with previously-downloaded sheets so the loop doesn't flag "Missing" on the old column.
                    "Reporting Manager",
                    // Internal Notes - always optional
                    "Internal Notes",
                    // Current Address - always optional
                    "Current Address Line 1", "Current Address Line 2",
                    "Current City", "Current State", "Current Pincode",
                    // Permanent Address - always optional
                    "Permanent Address Same As Current",
                    "Permanent Address Line 1", "Permanent Address Line 2",
                    "Permanent City", "Permanent State", "Permanent Pincode",
                    // EC1 - Email and Address fields are optional (core 4 names checked explicitly)
                    "EC1 Email",
                    "EC1 Address Same As",
                    "EC1 Address Line 1", "EC1 Address Line 2",
                    "EC1 City", "EC1 State", "EC1 Pincode",
                    // EC2 - fully optional
                    "EC2 First Name", "EC2 Last Name", "EC2 Relation", "EC2 Phone", "EC2 Email",
                    "EC2 Address Same As",
                    "EC2 Address Line 1", "EC2 Address Line 2",
                    "EC2 City", "EC2 State", "EC2 Pincode",
                    // EC3 - fully optional
                    "EC3 First Name", "EC3 Last Name", "EC3 Relation", "EC3 Phone", "EC3 Email",
                    "EC3 Address Same As",
                    "EC3 Address Line 1", "EC3 Address Line 2",
                    "EC3 City", "EC3 State", "EC3 Pincode",
                ];

                worksheet.eachRow((row, rowIndex) => {
                    const rowValues = row.values.slice(1);
                    // Detect header row by looking for known anchor columns
                    if (rowValues.includes("Employee ID") && rowValues.includes("Email") && rowValues.includes("DOB (DD-MM-YYYY)")) {
                        excelHeaders = rowValues.map(v => v?.toString().trim() || "");
                        return;
                    }
                    // Also support old template header "DOB" without the format note
                    if (rowValues.includes("Employee ID") && rowValues.includes("Email") && rowValues.includes("DOB")) {
                        excelHeaders = rowValues.map(v => v?.toString().trim() || "");
                        return;
                    }
                    if (excelHeaders.length === 0) return;
                    let rowObj = {};
                    excelHeaders.forEach((h, i) => { rowObj[h] = rowValues[i] !== undefined && rowValues[i] !== null ? getCellString(rowValues[i]) : ""; });
                    if (!Object.values(rowObj).some(v => v !== "")) return;
                    let errorReason = [];

                    // Update rows (Employee ID present) are still partial for OPTIONAL fields
                    // (blank = preserve DB value), but REQUIRED fields must be present in every
                    // row - matches the modal's handleSave which enforces them on both create AND
                    // update. To do partial updates, start from the exported file which already
                    // contains all required fields filled in.
                    const isUpdateRow = !!((rowObj["Employee ID"] || "").trim());

                    // Required field check (normalize header key - strip format hints).
                    // Anything NOT in allowedEmpty is required for every row (insert + update).
                    const normaliseKey = k => k.replace(/\s*\(.*?\)/g, "").trim();
                    excelHeaders.forEach(h => {
                        const norm = normaliseKey(h);
                        const isOptional = allowedEmpty.some(a => normaliseKey(a) === norm || a === h);
                        const val = rowObj[h];
                        if (!isOptional && (!val || val.trim() === "")) errorReason.push(`Missing ${h}`);
                    });
                    if (!rowObj["Account Password"]) errorReason.push("Missing Account Password");

                    // ── DOB validation ──
                    const dobKey = excelHeaders.find(h => h.startsWith("DOB"));
                    const dobRaw = rowObj[dobKey] || rowObj["DOB"] || "";
                    if (dobRaw) {
                        const parsed = parseFlexDate(dobRaw);
                        if (!parsed) {
                            errorReason.push("Invalid DOB format. Accepted: YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD, DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY");
                        } else {
                            const { year, month, day } = parsed;
                            const sel = new Date(year, month - 1, day); const tdy = new Date(); tdy.setHours(0, 0, 0, 0);
                            if (isNaN(sel.getTime())) errorReason.push("Invalid DOB value");
                            else if (sel >= tdy) errorReason.push("DOB cannot be today or a future date");
                            else {
                                const age = tdy.getFullYear() - year;
                                const hadBirthday = tdy.getMonth() > month - 1 || (tdy.getMonth() === month - 1 && tdy.getDate() >= day);
                                if ((hadBirthday ? age : age - 1) < 15) errorReason.push("Employee must be at least 15 years old");
                                else rowObj["DOB"] = toIsoDate(parsed);
                            }
                        }
                    }

                    // ── DOJ validation ── required for every row (matches modal)
                    // Accept the new "DOJ (DD-MM-YYYY)" header AND the legacy "Joining Date (DD-MM-YYYY)"
                    // header so previously-downloaded templates still upload cleanly.
                    const jdKey = excelHeaders.find(h => h.startsWith("DOJ") || h.startsWith("Joining Date"));
                    const jdRaw = rowObj[jdKey] || rowObj["DOJ"] || rowObj["Joining Date"] || "";
                    if (!jdRaw || jdRaw.trim() === "") {
                        errorReason.push("Missing DOJ");
                    } else {
                        const parsed = parseFlexDate(jdRaw);
                        if (!parsed) {
                            errorReason.push("Invalid DOJ format. Accepted: YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD, DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY");
                        } else {
                            // Backend reads `emp["Joining Date"]` - keep that as the canonical key
                            rowObj["Joining Date"] = toIsoDate(parsed);
                        }
                    }

                    // ── Passport Expiry validation (optional but must be future if provided) ──
                    const peKey = excelHeaders.find(h => h.startsWith("Passport Expiry"));
                    const peRaw = rowObj[peKey] || rowObj["Passport Expiry"] || "";
                    if (peRaw && peRaw.trim() !== "") {
                        const parsed = parseFlexDate(peRaw);
                        if (!parsed) {
                            errorReason.push("Invalid Passport Expiry format. Accepted: YYYY-MM-DD, YYYY/MM/DD, YYYY.MM.DD, DD-MM-YYYY, DD/MM/YYYY, DD.MM.YYYY");
                        } else {
                            const { year, month, day } = parsed;
                            const expDate = new Date(year, month - 1, day); const tdy = new Date(); tdy.setHours(0, 0, 0, 0);
                            if (expDate <= tdy) errorReason.push("Passport Expiry must be a future date");
                            else rowObj["Passport Expiry"] = toIsoDate(parsed);
                        }
                    }

                    // ── CTC validation - optional; only validate format if provided ──
                    const ctcRaw = rowObj["CTC (Annual)"] || rowObj["CTC"] || "";
                    if (ctcRaw && ctcRaw.trim() !== "") {
                        const ctcNum = Number(ctcRaw);
                        if (isNaN(ctcNum) || ctcNum <= 0) {
                            errorReason.push("CTC must be a positive number");
                        } else if (ctcNum > 99999999.99) {
                            // DB column is decimal(10,2) → max 99,999,999.99
                            errorReason.push("CTC cannot exceed 99,999,999.99");
                        } else {
                            rowObj["CTC"] = ctcRaw;
                        }
                    }

                    // Reporting Manager is no longer accepted from Excel - assigned via the
                    // Manager Allocation page. Any value in an old template's "Reporting Manager"
                    // column is silently ignored by the backend.

                    // ── Email, Mobile, Aadhar, Bank, IFSC, PAN validations ──
                    // Format checks are conditional on the value being present so update rows can
                    // leave fields blank without triggering format errors.
                    const email = getCellString(rowObj["Email"]);
                    if (email && !email.endsWith("@dolluzcorp.com")) errorReason.push("Invalid Email Domain");
                    if (rowObj["Mobile"] && !/^\d{10}$/.test(rowObj["Mobile"])) errorReason.push("Mobile must be 10 digits");
                    if (rowObj["Alternate Mobile"]) { if (!/^\d{10}$/.test(rowObj["Alternate Mobile"])) errorReason.push("Alternate Mobile must be 10 digits"); else if (rowObj["Mobile"] && rowObj["Mobile"] === rowObj["Alternate Mobile"]) errorReason.push("Alternate Mobile must differ from Mobile"); }
                    // Personal Email - if provided, must be valid format and differ from official Email (case-insensitive)
                    if (rowObj["Personal Email"]) {
                        const pe = rowObj["Personal Email"].trim().toLowerCase();
                        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pe)) errorReason.push("Personal Email format is invalid");
                        else if (email && pe === email.trim().toLowerCase()) errorReason.push("Personal Email must differ from official Email");
                    }
                    if (rowObj["Aadhar Number"] && !/^\d{12}$/.test(rowObj["Aadhar Number"])) errorReason.push("Aadhar must be 12 digits");
                    if (rowObj["Bank Name"] && /^\d+$/.test(rowObj["Bank Name"])) errorReason.push("Bank Name cannot be only numbers");
                    // Bank Account Number - digits only (matches the UI numeric-only input transform)
                    if (rowObj["Bank Account Number"] && !/^\d+$/.test(rowObj["Bank Account Number"])) errorReason.push("Bank Account Number must contain only digits");
                    if (rowObj["IFSC Code"] && !/^[A-Za-z]{4}\d{7}$/.test(rowObj["IFSC Code"])) errorReason.push("Invalid IFSC Code Format");
                    if (rowObj["PAN Number"] && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(rowObj["PAN Number"].toUpperCase())) errorReason.push("Invalid PAN format (e.g. ABCDE1234F)");
                    // Passport Number - uppercase alphanumeric, max 9 (matches the UI upperAlnum + slice(0,9) transform)
                    if (rowObj["Passport Number"] && !/^[A-Za-z0-9]{1,9}$/.test(rowObj["Passport Number"])) errorReason.push("Passport Number must be alphanumeric (max 9 characters)");
                    const eid = rowObj["Employee ID"];
                    if (eid && !/^DZIND\d{3}$/.test(eid)) errorReason.push("Employee ID must be DZIND followed by exactly 3 digits");

                    // ── DB varchar length caps - guard against SQL truncation/errors ──
                    // [excel header, human label, max chars per DB schema]
                    const bulkLengthChecks = [
                        ["First Name", "First Name", 100],
                        ["Last Name", "Last Name", 100],
                        ["Email", "Email", 150],
                        ["Personal Email", "Personal Email", 180],
                        ["Nationality", "Nationality", 60],
                        ["Account Password", "Account Password", 255],
                        ["Bank Name", "Bank Name", 100],
                        ["Bank Account Number", "Bank Account Number", 50],
                        ["Passport Number", "Passport Number", 20],
                        ["Passport Country", "Passport Country", 100],
                        // Reporting Manager dropped - no longer ingested from Excel.
                        ["Current Address Line 1", "Current Address Line 1", 200],
                        ["Current Address Line 2", "Current Address Line 2", 200],
                        ["Current City", "Current City", 100],
                        ["Current State", "Current State", 100],
                        ["Permanent Address Line 1", "Permanent Address Line 1", 200],
                        ["Permanent Address Line 2", "Permanent Address Line 2", 200],
                        ["Permanent City", "Permanent City", 100],
                        ["Permanent State", "Permanent State", 100],
                    ];
                    bulkLengthChecks.forEach(([key, label, max]) => {
                        const v = rowObj[key];
                        if (v && String(v).length > max) errorReason.push(`${label} cannot exceed ${max} characters`);
                    });
                    // EC1/EC2/EC3 - same shape for each
                    const ecBulkLengths = [
                        ["First Name", "First Name", 60],
                        ["Last Name", "Last Name", 60],
                        ["Relation", "Relation", 60],
                        ["Email", "Email", 180],
                        ["Address Line 1", "Address Line 1", 200],
                        ["Address Line 2", "Address Line 2", 200],
                        ["City", "City", 100],
                        ["State", "State", 100],
                    ];
                    ["EC1", "EC2", "EC3"].forEach(ec => {
                        ecBulkLengths.forEach(([suffix, label, max]) => {
                            const headerName = `${ec} ${suffix}`;
                            const v = rowObj[headerName];
                            if (v && String(v).length > max) errorReason.push(`${ec} ${label} cannot exceed ${max} characters`);
                        });
                    });

                    // ── Address fields - pincodes if filled must be 6 digits ──
                    if (rowObj["Current Pincode"] && !/^\d{6}$/.test(rowObj["Current Pincode"])) errorReason.push("Current Pincode must be 6 digits");
                    if (rowObj["Permanent Pincode"] && !/^\d{6}$/.test(rowObj["Permanent Pincode"])) errorReason.push("Permanent Pincode must be 6 digits");
                    // Permanent Address Same As Current - normalize Yes/No
                    const permSameRaw = (rowObj["Permanent Address Same As Current"] || "").toString().trim().toLowerCase();
                    if (permSameRaw === "yes") rowObj["Permanent Address Same As Current"] = 1;
                    else if (permSameRaw === "no") rowObj["Permanent Address Same As Current"] = 0;
                    else rowObj["Permanent Address Same As Current"] = ""; // blank → backend skips on update / defaults to 0 on insert

                    // ── EC1 - mandatory core 4 fields for every row (insert AND update)
                    if (!rowObj["EC1 First Name"]) errorReason.push("Missing EC1 First Name");
                    if (!rowObj["EC1 Last Name"])  errorReason.push("Missing EC1 Last Name");
                    if (!rowObj["EC1 Relation"])   errorReason.push("Missing EC1 Relation");
                    if (!rowObj["EC1 Phone"])      errorReason.push("Missing EC1 Phone");
                    if (rowObj["EC1 Phone"] && !/^\d{10}$/.test(rowObj["EC1 Phone"])) errorReason.push("EC1 Phone must be 10 digits");
                    if (rowObj["EC1 Email"] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rowObj["EC1 Email"])) errorReason.push("EC1 Email format is invalid");
                    if (rowObj["EC1 Pincode"] && !/^\d{6}$/.test(rowObj["EC1 Pincode"])) errorReason.push("EC1 Pincode must be 6 digits");

                    // ── EC2 / EC3 - fully optional; format checks only when filled ──
                    ["EC2", "EC3"].forEach(ec => {
                        if (rowObj[`${ec} Phone`] && !/^\d{10}$/.test(rowObj[`${ec} Phone`])) errorReason.push(`${ec} Phone must be 10 digits`);
                        if (rowObj[`${ec} Email`] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(rowObj[`${ec} Email`])) errorReason.push(`${ec} Email format is invalid`);
                        if (rowObj[`${ec} Pincode`] && !/^\d{6}$/.test(rowObj[`${ec} Pincode`])) errorReason.push(`${ec} Pincode must be 6 digits`);
                    });

                    // EC Address Same As - normalize "Current"/"Permanent"/"" (case-insensitive); reject anything else
                    ["EC1", "EC2", "EC3"].forEach(ec => {
                        const raw = (rowObj[`${ec} Address Same As`] || "").toString().trim().toLowerCase();
                        if (raw === "" || raw === "current" || raw === "permanent") {
                            rowObj[`${ec} Address Same As`] = raw; // "" / "current" / "permanent"
                        } else {
                            errorReason.push(`${ec} Address Same As must be "Current", "Permanent", or blank`);
                        }
                    });

                    // ── Active field: normalise Yes/No → 1/0; leave blank as "" so backend skips on UPDATE
                    const activeRaw = (rowObj["Active"] || "").toString().trim();
                    if (activeRaw === "") {
                        rowObj["Active"] = ""; // signal: don't touch this column on update / default to 1 on insert
                    } else if (activeRaw.toLowerCase() === "no") {
                        rowObj["Active"] = 0;
                    } else {
                        rowObj["Active"] = 1;
                    }

                    parsedRows.push({ rowIndex, rowObj, errors: errorReason });
                });

                // ── Whole-sheet checks ────────────────────────────────────────
                // The backend runs these too, but it can only reach them once
                // every per-row check has passed. Duplicating them here means a
                // single upload surfaces per-row AND cross-row problems together,
                // instead of making the user fix one class, re-upload, and
                // discover the next.
                const dupValues = (key) => {
                    const counts = new Map();
                    parsedRows.forEach(({ rowObj }) => {
                        const v = (rowObj[key] || "").toString().trim();
                        if (!v) return;
                        counts.set(v, (counts.get(v) || 0) + 1);
                    });
                    return new Set([...counts].filter(([, n]) => n > 1).map(([v]) => v));
                };
                const dupIds = dupValues("Employee ID");
                const dupMobiles = dupValues("Mobile");

                parsedRows.forEach(({ rowObj, errors }) => {
                    const id = (rowObj["Employee ID"] || "").toString().trim();
                    if (id && dupIds.has(id)) errors.push(`Employee ID ${id} is repeated on more than one row`);
                    const mobile = (rowObj["Mobile"] || "").toString().trim();
                    if (mobile && dupMobiles.has(mobile)) errors.push(`Mobile ${mobile} is repeated on more than one row`);
                });

                parsedRows.forEach(({ rowIndex, rowObj, errors }) => {
                    if (errors.length > 0) errorData.push({ RowIndex: rowIndex, EmpID: rowObj["Employee ID"] || "N/A", Errors: errors, RowData: rowObj });
                    // __excelRow rides along so a backend error naming an emp_id,
                    // mobile, department, or person can be traced back to the exact
                    // sheet row. Stripped again before the payload is POSTed.
                    else finalData.push({ ...rowObj, __excelRow: rowIndex });
                });

                if (finalData.length === 0 && errorData.length === 0) { dnAlert.warning("No Data Found", "Please enter at least one employee record."); return; }
                if (errorData.length > 0) {
                    // Client-side validation caught it - we already know the exact
                    // sheet row for every problem, so hand the modal the full detail.
                    setBulkErrors({
                        title: "Validation Failed",
                        note: `${errorData.length} row${errorData.length === 1 ? " has" : "s have"} errors. Nothing was saved. Fix them in the Excel file and upload again.`,
                        rows: errorData.map(err => ({
                            excelRow: err.RowIndex,
                            empId: err.RowData?.["Employee ID"] || "-",
                            name: [err.RowData?.["First Name"], err.RowData?.["Last Name"]].filter(Boolean).join(" ") || "-",
                            email: err.RowData?.["Email"] || "-",
                            errors: err.Errors,
                        })),
                    });
                } else {
                    try {
                        // __excelRow stays on the payload on purpose - the backend
                        // echoes it back so DB-only failures name the sheet row.
                        const response = await apiFetch("/api/employee/bulk-create", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ employees: finalData }) });
                        let data; try { data = await response.json(); } catch { throw { message: "Server Error: Invalid response format" }; }
                        if (data.success) {
                            const ins = data.inserted || 0;
                            const upd = data.updated || 0;
                            const parts = [];
                            if (ins > 0) parts.push(`${ins} new employee${ins !== 1 ? "s" : ""} added`);
                            if (upd > 0) parts.push(`${upd} existing employee${upd !== 1 ? "s" : ""} updated`);
                            const msg = parts.length > 0 ? parts.join(", ") : "No changes applied";
                            dnAlert.success("Success", msg);
                            setShowBulkModal(false);
                            fetchEmployees();
                        }
                        else throw data;
                    } catch (error) {
                        const serverError = error.sqlMessage || error.reason || error.error || error.message || "Unknown Server Error";
                        const rows = resolveServerErrorRows(error, serverError, finalData);

                        setBulkErrors({
                            title: "Upload Failed",
                            note: rows.length
                                ? `${rows.length} row${rows.length === 1 ? "" : "s"} rejected by the server. Nothing was saved. Fix them in the Excel file and upload again.`
                                : "The server rejected the upload. Nothing was saved.",
                            rows: rows.length
                                ? rows
                                : [{ excelRow: "-", empId: "-", name: "Server / Database", email: "-", errors: [serverError] }],
                        });
                    }
                }
              } finally {
                // clears the loader on every exit path (validation error, success,
                // server error, or early return) since they're all inside the try above
                setActionLoading(null);
              }
            };
            reader.readAsArrayBuffer(excelFile);
        } catch (error) {
            console.error("❌ Excel Reading Failed:", error);
            setActionLoading(null);
        }
    };

    const generatePassword = () => {
        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$&*!";
        return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    };

    const today = new Date();
    const maxDOB = new Date(); maxDOB.setFullYear(today.getFullYear() - 15);
    const maxDOBFormatted = maxDOB.toISOString().split("T")[0];

    // ─────────────────────────────────────────────────────────────────────────────
    return (
        <div className="employee-dashboard-container">
            {/* Blocking loader overlay for import/export/download actions */}
            {actionLoading && (
                <div className="ec-loader-overlay">
                    <div className="ec-loader-spinner" />
                    <div className="ec-loader-text">{actionLoading}</div>
                </div>
            )}
            <div className={`employee-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        Employee Center
                    </h2>
                    <p className="dspr-subtitle">
                        Master record for every employee. Add, edit, soft-delete, bulk-import via Excel,
                        and export to PDF / Excel. Filter, sort, and search to find anyone instantly.
                    </p>
                </div>

                <div className="employee-dashboard-content">
                    {/* ── Filters ── */}
                    <div className="employee-filters-container">
                        <div className="employee-filters-left">
                            <Select
                                options={filterOptions}
                                value={filterOptions.find(f => f.value === selectedFilter)}
                                onChange={val => { setSelectedFilter(val.value); setFilters(prev => ({ ...prev, [val.value]: [] })); setCurrentPage(1); }}
                                placeholder="Select filter" isSearchable
                                menuPortalTarget={document.body}
                                styles={{
                                    container: p => ({ ...p, width: 220 }),
                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                }}
                            />

                            {selectedFilter === "created_time" ? (
                                <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                                    <input type="date" value={fromDate} onClick={e => e.target.showPicker?.()} onChange={e => { setFromDate(e.target.value); setCurrentPage(1); }} className="date-input" />
                                    <span><b>to</b></span>
                                    <input type="date" value={toDate} onClick={e => e.target.showPicker?.()} onChange={e => { setToDate(e.target.value); setCurrentPage(1); }} className="date-input" />
                                    {(fromDate || toDate) && <FaTimes size={18} style={{ cursor: "pointer", color: "#555" }} onClick={() => { setFromDate(""); setToDate(""); setCurrentPage(1); }} title="Clear" />}
                                </div>
                            ) : (
                                <Select
                                    placeholder={`Filter by ${filterOptions.find(f => f.value === selectedFilter)?.label}`}
                                    options={getOptions(selectedFilter)} value={filters[selectedFilter]}
                                    onChange={vals => setFilters(prev => ({ ...prev, [selectedFilter]: vals || [] }))}
                                    closeMenuOnSelect={false} isMulti hideSelectedOptions={false} isClearable components={{ Option: CheckboxOption }}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        container: p => ({ ...p, width: 250 }),
                                        multiValue: p => ({ ...p, backgroundColor: "#e0e0e0" }),
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                    }}
                                />
                            )}

                            {/* ── Sort Dropdown ── */}
                            <div style={{ position: "relative" }} data-sort-dropdown>
                                <button
                                    onClick={() => setShowSortDropdown(p => !p)}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6,
                                        padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db",
                                        background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500,
                                        color: "#374151", whiteSpace: "nowrap",
                                        boxShadow: showSortDropdown ? "0 0 0 2px #f97316aa" : "none",
                                        outline: showSortDropdown ? "2px solid #f9731655" : "none",
                                    }}
                                >
                                    <span style={{ fontSize: 15 }}>&#8645;</span>
                                    {SORT_OPTIONS.find(o => o.value === sortOrder)?.label || "Sort by…"}
                                    <span style={{ fontSize: 10, marginLeft: 2 }}>▼</span>
                                </button>
                                {showSortDropdown && (
                                    <div style={{
                                        position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 999,
                                        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)", minWidth: 200, overflow: "hidden",
                                    }}>
                                        <div style={{ padding: "8px 12px", fontSize: 11, fontWeight: 600, color: "#9ca3af", borderBottom: "1px solid #f3f4f6", letterSpacing: "0.05em" }}>
                                            &#8645; Sort by…
                                        </div>
                                        {SORT_OPTIONS.map(opt => (
                                            <div
                                                key={opt.value}
                                                onClick={() => { setSortOrder(opt.value); setShowSortDropdown(false); setCurrentPage(1); }}
                                                style={{
                                                    padding: "9px 16px", fontSize: 13, cursor: "pointer",
                                                    background: sortOrder === opt.value ? "#1d4ed8" : "transparent",
                                                    color: sortOrder === opt.value ? "#fff" : "#374151",
                                                    fontWeight: sortOrder === opt.value ? 600 : 400,
                                                    transition: "background 0.15s",
                                                }}
                                                onMouseEnter={e => { if (sortOrder !== opt.value) e.currentTarget.style.background = "#f3f4f6"; }}
                                                onMouseLeave={e => { if (sortOrder !== opt.value) e.currentTarget.style.background = "transparent"; }}
                                            >
                                                {opt.label}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <FaUserPlus size={22} color="#3498db" title="Add Employee" style={{ cursor: "pointer" }}
                                onClick={() => {
                                    setShowModal(true);
                                    setEditingEmp(null);
                                    setResumeFile(null);
                                    setResumePendingDelete(false);
                                    setFormData({ ...EMPTY_FORM, ec1: { ...EMPTY_EC }, ec2: { ...EMPTY_EC }, ec3: { ...EMPTY_EC } });
                                    setErrors({});
                                    setEcCount(1);
                                    setOpenSections(new Set(["basic"]));
                                }} />
                            <FaUsers size={25} color="#3498db" title="Add Multiple Employees" style={{ cursor: "pointer" }} onClick={() => setShowBulkModal(true)} />

                            {/* Bulk Modal */}
                            {showBulkModal && (
                                <div className="bulk-modal-overlay">
                                    <div className="bulk-modal-content">
                                        <h3>Upload multiple employees</h3>
                                        <div style={{ marginBottom: "10px", textAlign: "center" }}>
                                            <button
                                                disabled={!!actionLoading}
                                                onClick={() => runWithLoader("Preparing template…", async () => {
                                                    const res = await apiFetch("/api/employee/get-employee-template", { credentials: "include" });
                                                    if (!res.ok) { dnAlert.error("Download failed", "Failed to download template - please try again."); return; }
                                                    const blob = await res.blob();
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement("a");
                                                    a.href = url; a.download = "dAppsEmployeeUploadTemplate.xlsx";
                                                    document.body.appendChild(a); a.click();
                                                    document.body.removeChild(a); URL.revokeObjectURL(url);
                                                })}
                                                style={{ background: "none", border: "none", cursor: actionLoading ? "not-allowed" : "pointer", color: "#007bff", fontWeight: "600", fontSize: "inherit", padding: 0, opacity: actionLoading ? 0.5 : 1 }}
                                            >
                                                📥 Download Employee Template
                                            </button>
                                        </div>
                                        <div className="bulk-modal-fields">
                                            <label>Select Excel File (.xlsx or .xls)</label>
                                            <input type="file" accept=".xlsx, .xls" onChange={e => setExcelFile(e.target.files[0])} style={{ marginTop: "8px", width: "100%" }} />
                                        </div>
                                        <div className="bulk-modal-footer">
                                            <button className="bulk-btn-cancel" onClick={() => setShowBulkModal(false)} disabled={!!actionLoading}>Cancel</button>
                                            <button className="bulk-btn-upload" onClick={handleBulkUpload} disabled={!excelFile || !!actionLoading}>{actionLoading ? "Uploading…" : "Upload"}</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Row-level bulk-upload error report. Stacks above the bulk
                                modal, which stays open so the user can re-upload the fixed file. */}
                            <BulkErrorModal report={bulkErrors} onClose={() => setBulkErrors(null)} />

                            <FaFilePdf size={24} color="#e74c3c" title="Export as PDF"
                                onClick={() => runWithLoader("Generating PDF…", () => exportEmployeesToPDF(sortedEmployees))}
                                style={{ cursor: actionLoading ? "not-allowed" : "pointer", opacity: actionLoading ? 0.5 : 1 }} />
                            <FaFileExcel size={24} color="#27ae60" title="Export as Excel"
                                onClick={() => runWithLoader("Generating Excel…", () => exportEmployeesToExcel(sortedEmployees))}
                                style={{ cursor: actionLoading ? "not-allowed" : "pointer", opacity: actionLoading ? 0.5 : 1 }} />

                            {/* Rows-per-page selector - sits at the right end of the toolbar.
                                Changing this resets the pager back to page 1 so we don't land on
                                a page that no longer exists at the new page size. */}
                            <div className="rows-per-page">
                                <span className="rows-per-page-label">Show</span>
                                <ScrollSelect
                                    value={rowsPerPage}
                                    options={ROWS_PER_PAGE_OPTIONS.map(n => ({ value: n, label: String(n) }))}
                                    minWidth={80}
                                    onChange={(v) => { setRowsPerPage(Number(v)); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── Table ── */}
                    <div className="table-top-wrapper">
                        <div className="employee-table-wrapper">
                            <table className="employee-table">
                                <thead>
                                    <tr>
                                        <th>Employee ID</th><th>First Name</th><th>Last Name</th><th>Email</th><th>Mobile</th>
                                        <th>Department</th><th>Employee Type</th><th>Location</th><th>Access</th>
                                        <th>CTC (₹)</th><th>DOJ</th><th>Reporting Manager</th><th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="14" style={{ textAlign: "center" }}>Loading...</td></tr>
                                    ) : paginatedEmployees.map(emp => (
                                        // The Status column sits far right and is usually scrolled
                                        // out of view, so inactive rows carry their own muted styling.
                                        <tr key={emp.emp_id} className={emp.active ? "" : "row-inactive"}>
                                            <td>{emp.emp_id}</td>
                                            <td>{emp.emp_first_name}</td>
                                            <td>{emp.emp_last_name}</td>
                                            <td>{emp.emp_mail_id}</td>
                                            <td>{emp.emp_mobile_no}</td>
                                            <td>{emp.emp_department}</td>
                                            <td>{emp.emp_type}</td>
                                            <td>{emp.emp_location}</td>
                                            <td>{emp.emp_access_level}</td>
                                            <td>{emp.ctc ? `₹${Number(emp.ctc).toLocaleString("en-IN")}` : "-"}</td>
                                            <td>{emp.joining_date ? emp.joining_date.split("T")[0] : "-"}</td>
                                            <td>{formatReportingManager(emp)}</td>
                                            <td>
                                                <span style={{
                                                    display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                                                    backgroundColor: emp.active ? "#d4edda" : "#f8d7da",
                                                    color: emp.active ? "#155724" : "#721c24"
                                                }}>
                                                    {emp.active ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <FaEdit className="ec-row-action ec-row-action--edit"   onClick={() => openEditModal(emp)} />
                                                    <FaTrash className="ec-row-action ec-row-action--delete" onClick={() => handleDelete(emp.emp_id)} />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Pagination ── */}
                    <div style={{ marginTop: "20px", display: "flex", justifyContent: "center", gap: "10px" }}>
                        <button className="pagination_btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        <span style={{ marginTop: "8px" }}>Page {currentPage} of {totalPages}</span>
                        <button className="pagination_btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                    </div>
                </div>

                {/* ── Employee Modal ── */}
                {showModal && (
                    <div className="employee-modal-overlay">
                        <div className="employee-modal-content" onClick={e => e.stopPropagation()}>
                            <div className="close-icon" onClick={() => setShowModal(false)} title="Close"><i className="fa-solid fa-xmark"></i></div>
                            <h3>{editingEmp ? "Edit Employee" : "Add Employee"}</h3>

                            {/* ══ SECTION 1: Basic Info ══ */}
                            <SectionHeader id="basic" title="👤 Basic Information" openSections={openSections} toggleSection={toggleSection} />
                            {openSections.has("basic") && (
                                <div className="modal-fields">
                                    <div className="field-group">
                                        <label>First Name <span className="required">*</span></label>
                                        <input type="text" name="emp_first_name" value={formData.emp_first_name} onChange={handleInputChange} className={errors.emp_first_name ? "input-error" : ""} placeholder="First name" />
                                    </div>
                                    <div className="field-group">
                                        <label>Last Name <span className="required">*</span></label>
                                        <input type="text" name="emp_last_name" value={formData.emp_last_name} onChange={handleInputChange} className={errors.emp_last_name ? "input-error" : ""} placeholder="Last name" />
                                    </div>
                                    <div className="field-group">
                                        <label>Official Mail ID <span className="required">*</span></label>
                                        <input type="email" name="emp_mail_id" value={formData.emp_mail_id} onChange={handleInputChange} className={errors.emp_mail_id ? "input-error" : ""} placeholder="example@dolluzcorp.com" />
                                    </div>
                                    <div className="field-group">
                                        <label>Personal Email</label>
                                        <input type="email" name="personal_email" value={formData.personal_email} onChange={handleInputChange} className={errors.personal_email ? "input-error" : ""} placeholder="personal@example.com" />
                                    </div>
                                    <div className="field-group">
                                        <label>Gender</label>
                                        <Select
                                            options={genderOptions}
                                            value={genderOptions.find(o => o.value === formData.gender) || null}
                                            onChange={s => handleInputChange({ target: { name: "gender", value: s ? s.value : "" } })}
                                            placeholder="Select gender"
                                            isSearchable
                                            isClearable
                                        />
                                    </div>
                                    <div className="field-group">
                                        <label>Nationality</label>
                                        <input type="text" name="nationality" value={formData.nationality} onChange={handleInputChange} placeholder="e.g. Indian" />
                                    </div>
                                    <div className="field-group">
                                        <label>Location <span className="required">*</span></label>
                                        <Select
                                            options={locationOptions}
                                            value={locationOptions.find(o => o.value === formData.emp_location) || null}
                                            onChange={s => handleInputChange({ target: { name: "emp_location", value: s ? s.value : "" } })}
                                            placeholder="Select location"
                                            isSearchable
                                            isClearable
                                            className={errors.emp_location ? "select-error" : ""}
                                        />
                                    </div>
                                    <div className="field-group">
                                        <label>Mobile <span className="required">*</span></label>
                                        <input type="text" name="emp_mobile_no" maxLength={10} value={formData.emp_mobile_no} onChange={handleInputChange} className={errors.emp_mobile_no ? "input-error" : ""} placeholder="10-digit mobile" />
                                    </div>
                                    <div className="field-group">
                                        <label>Alternate Mobile</label>
                                        <input type="text" name="emp_alternate_mobile_no" maxLength={10} value={formData.emp_alternate_mobile_no} onChange={handleInputChange} className={errors.emp_alternate_mobile_no ? "input-error" : ""} placeholder="10-digit mobile (optional)" />
                                    </div>
                                    <div className="field-group">
                                        <label>DOB <span className="required">*</span></label>
                                        <input type="date" name="dob" value={formData.dob} max={maxDOBFormatted}
                                            onClick={e => e.target.showPicker?.()}
                                            onChange={e => {
                                                const val = e.target.value; const d = new Date(val); const min = new Date(maxDOBFormatted);
                                                setErrors(prev => ({ ...prev, dob: d > min, dobMessage: d > min ? "Must be at least 15 years old" : "" }));
                                                setFormData(prev => ({ ...prev, dob: val }));
                                            }}
                                            className={errors.dob ? "input-error" : ""} />
                                        {errors.dobMessage && <span className="error-note">{errors.dobMessage}</span>}
                                    </div>
                                    <div className="field-group">
                                        <label>Blood Group</label>
                                        <Select options={bloodGroupOptions} value={bloodGroupOptions.find(o => o.value === formData.blood_group)}
                                            onChange={s => handleInputChange({ target: { name: "blood_group", value: s.value } })}
                                            placeholder="Select blood group (optional)" className={errors.blood_group ? "select-error" : ""} isSearchable isClearable />
                                    </div>
                                    <div className="field-group">
                                        <label>Department <span className="required">*</span></label>
                                        <Select options={options.department} value={options.department.find(o => o.value === formData.emp_department)}
                                            onChange={s => handleInputChange({ target: { name: "emp_department", value: s.value } })}
                                            placeholder="Select department" className={errors.emp_department ? "select-error" : ""} isSearchable />
                                    </div>
                                    <div className="field-group">
                                        <label>Employee Type <span className="required">*</span></label>
                                        <Select options={options.type} value={options.type.find(o => o.value === formData.emp_type)}
                                            onChange={s => handleInputChange({ target: { name: "emp_type", value: s.value } })}
                                            placeholder="Select type" className={errors.emp_type ? "select-error" : ""} isSearchable />
                                    </div>
                                    <div className="field-group">
                                        <label>Job Position <span className="required">*</span></label>
                                        <Select options={options.job} value={options.job.find(o => o.value === formData.job_position)}
                                            onChange={s => handleInputChange({ target: { name: "job_position", value: s.value } })}
                                            placeholder="Select job position" className={errors.job_position ? "select-error" : ""} isSearchable />
                                    </div>
                                    <div className="field-group">
                                        <label>Career Level <span className="required">*</span></label>
                                        <Select options={options.level} value={options.level.find(o => o.value === formData.career_level)}
                                            onChange={s => handleInputChange({ target: { name: "career_level", value: s.value } })}
                                            placeholder="Select career level" className={errors.career_level ? "select-error" : ""} isSearchable />
                                    </div>
                                    <div className="field-group">
                                        <label>CTC (Annual ₹)</label>
                                        <input type="number" name="ctc" value={formData.ctc} onChange={handleInputChange} className={errors.ctc ? "input-error" : ""} placeholder="e.g. 500000 (optional)" min="0" />
                                    </div>
                                    <div className="field-group">
                                        <label>DOJ <span className="required">*</span></label>
                                        <input type="date" name="joining_date" value={formData.joining_date} onClick={e => e.target.showPicker?.()} onChange={handleInputChange} className={errors.joining_date ? "input-error" : ""} />
                                    </div>
                                    {/* Reporting Manager field removed - assigned via the Manager Allocation page (Manage Employees → Manager Allocation). */}
                                    <div className="status-toggle-row">
                                        <span className="status-toggle-label">Employment Status</span>
                                        <div className="status-toggle-pill-wrap">
                                            <div
                                                className={`status-toggle-pill ${formData.active ? "on" : "off"}`}
                                                onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
                                                title={formData.active ? "Click to deactivate" : "Click to activate"}
                                            >
                                                <div className="status-toggle-thumb" />
                                            </div>
                                            <span className={`status-toggle-text ${formData.active ? "active-text" : "inactive-text"}`}>
                                                {formData.active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="field-group">
                                        <label>Employee ID</label>
                                        <input type="text" name="emp_id" value={formData.emp_id} onChange={handleInputChange}
                                            className={errors.emp_id ? "input-error" : ""} placeholder="Auto-generated if blank"
                                            disabled={!!editingEmp} title={editingEmp ? "Cannot change after creation" : ""}
                                            style={editingEmp ? { backgroundColor: "#f0f0f0", cursor: "not-allowed", opacity: 0.7 } : {}} />
                                        <span style={{ fontSize: "11px", color: "#888", marginTop: "4px", display: "block" }}>⚠️ Employee ID cannot be changed after creation</span>
                                    </div>
                                    <div className="field-group">
                                        <label>Access Level <span className="required">*</span></label>
                                        <Select
                                            options={accessLevelOptions}
                                            value={accessLevelOptions.find(o => o.value === formData.emp_access_level) || null}
                                            onChange={s => handleInputChange({ target: { name: "emp_access_level", value: s ? s.value : "" } })}
                                            placeholder="Select access level"
                                            isSearchable
                                            isClearable
                                            className={errors.emp_access_level ? "select-error" : ""}
                                        />
                                    </div>
                                    <div className="field-group password-field" style={{ flex: "1 1 100%" }}>
                                        <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <span>Account Password <span className="required">*</span></span>
                                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <i className="fa-solid fa-circle-info" style={{ color: "#007bff" }} title="Click to auto-generate" />
                                                <span style={{ color: "#007bff", textDecoration: "underline", cursor: "pointer", fontSize: "0.95rem" }}
                                                    onClick={() => setFormData(p => ({ ...p, account_pass_text: generatePassword() }))}>Auto-generate Password</span>
                                            </div>
                                        </label>
                                        <div className="password-input-wrapper">
                                            <input type={showPassword ? "text" : "password"} name="account_pass_text" value={formData.account_pass_text}
                                                onChange={handleInputChange} className={errors.account_pass_text ? "input-error" : ""} placeholder="Enter account password" />
                                            <span className={`copy-icon ${!formData.account_pass_text ? "disabled" : ""}`}
                                                onClick={() => { if (formData.account_pass_text) { navigator.clipboard.writeText(formData.account_pass_text); setCopied(true); setTimeout(() => setCopied(false), 2000); } }}>
                                                <i className="fa-solid fa-copy" />
                                            </span>
                                            <span className={`password-toggle-icon ${!formData.account_pass_text ? "disabled" : ""}`} onClick={() => setShowPassword(!showPassword)}>
                                                <i className={`fa-solid ${showPassword ? "fa-eye" : "fa-eye-slash"}`} />
                                            </span>
                                        </div>
                                        {copied && <div className="copy-message">Password copied to clipboard</div>}
                                    </div>
                                </div>
                            )}

                            {/* ══ SECTION 2: Address ══ */}
                            <SectionHeader id="address" title="🏠 Address Details" openSections={openSections} toggleSection={toggleSection} />
                            {openSections.has("address") && (
                                <div className="modal-fields">
                                    <div style={{ width: "100%" }}><div className="section-title">Current address</div></div>
                                    <div className="field-group" style={{ flex: "1 1 100%" }}>
                                        <label>Address Line 1</label>
                                        <input type="text" name="curr_addr_line1" value={formData.curr_addr_line1} onChange={handleInputChange} placeholder="Street / area" />
                                    </div>
                                    <div className="field-group" style={{ flex: "1 1 100%" }}>
                                        <label>Address Line 2</label>
                                        <input type="text" name="curr_addr_line2" value={formData.curr_addr_line2} onChange={handleInputChange} placeholder="Landmark / flat No." />
                                    </div>
                                    <div className="field-group">
                                        <label>City</label>
                                        <input type="text" name="curr_addr_city" value={formData.curr_addr_city} onChange={handleInputChange} placeholder="City" />
                                    </div>
                                    <div className="field-group">
                                        <label>State</label>
                                        <input type="text" name="curr_addr_state" value={formData.curr_addr_state} onChange={handleInputChange} placeholder="State" />
                                    </div>
                                    <div className="field-group">
                                        <label>Pincode</label>
                                        <input type="text" name="curr_addr_pincode" maxLength={6} value={formData.curr_addr_pincode} onChange={handleInputChange} placeholder="6-digit pincode" />
                                    </div>

                                    <div style={{ width: "100%", marginTop: 8 }}>
                                        <div className="section-title">Permanent address</div>
                                        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, cursor: "pointer", fontWeight: 500 }}>
                                            <input type="checkbox" checked={formData.perm_addr_same_as_curr} onChange={handlePermSameToggle} style={{ width: 16, height: 16 }} />
                                            Same as Current Address
                                        </label>
                                    </div>
                                    <div className="field-group" style={{ flex: "1 1 100%" }}>
                                        <label>Address Line 1</label>
                                        <input type="text" name="perm_addr_line1" value={formData.perm_addr_line1} onChange={handleInputChange}
                                            placeholder="Street / area" readOnly={formData.perm_addr_same_as_curr}
                                            style={formData.perm_addr_same_as_curr ? { background: "#f5f5f5" } : {}} />
                                    </div>
                                    <div className="field-group" style={{ flex: "1 1 100%" }}>
                                        <label>Address Line 2</label>
                                        <input type="text" name="perm_addr_line2" value={formData.perm_addr_line2} onChange={handleInputChange}
                                            placeholder="Landmark / flat No." readOnly={formData.perm_addr_same_as_curr}
                                            style={formData.perm_addr_same_as_curr ? { background: "#f5f5f5" } : {}} />
                                    </div>
                                    <div className="field-group">
                                        <label>City</label>
                                        <input type="text" name="perm_addr_city" value={formData.perm_addr_city} onChange={handleInputChange}
                                            placeholder="City" readOnly={formData.perm_addr_same_as_curr}
                                            style={formData.perm_addr_same_as_curr ? { background: "#f5f5f5" } : {}} />
                                    </div>
                                    <div className="field-group">
                                        <label>State</label>
                                        <input type="text" name="perm_addr_state" value={formData.perm_addr_state} onChange={handleInputChange}
                                            placeholder="State" readOnly={formData.perm_addr_same_as_curr}
                                            style={formData.perm_addr_same_as_curr ? { background: "#f5f5f5" } : {}} />
                                    </div>
                                    <div className="field-group">
                                        <label>Pincode</label>
                                        <input type="text" name="perm_addr_pincode" maxLength={6} value={formData.perm_addr_pincode} onChange={handleInputChange}
                                            placeholder="6-digit pincode" readOnly={formData.perm_addr_same_as_curr}
                                            style={formData.perm_addr_same_as_curr ? { background: "#f5f5f5" } : {}} />
                                    </div>
                                </div>
                            )}

                            {/* ══ SECTION 3: Bank & KYC ══ */}
                            <SectionHeader id="bank" title="🏦 Bank & KYC Details" openSections={openSections} toggleSection={toggleSection} />
                            {openSections.has("bank") && (
                                <div className="modal-fields">
                                    <div className="bank-kyc-wrapper" style={{ width: "100%" }}>
                                        <div className="bank-column">
                                            <div className="section-title">Bank details</div>
                                            <div className="field-group">
                                                <label>Bank Name <span className="required">*</span></label>
                                                <input type="text" name="bank_name" value={formData.bank_name} onChange={handleInputChange} placeholder="Enter bank name" className={errors.bank_name ? "input-error" : ""} />
                                            </div>
                                            <div className="field-group">
                                                <label>Bank Account Number <span className="required">*</span></label>
                                                <input type="text" name="bank_account_number" value={formData.bank_account_number} onChange={handleInputChange} placeholder="Enter account number" className={errors.bank_account_number ? "input-error" : ""} />
                                            </div>
                                            <div className="field-group">
                                                <label>IFSC Code <span className="required">*</span></label>
                                                <input type="text" name="ifsc_code" value={formData.ifsc_code} onChange={handleInputChange} placeholder="e.g. SBIN0001234" className={errors.ifsc_code ? "input-error" : ""} />
                                            </div>
                                        </div>
                                        <div className="kyc-column">
                                            <div className="section-title">KYC Details</div>
                                            <div className="field-group">
                                                <label>Aadhar Number <span className="required">*</span></label>
                                                <input type="text" name="aadhar_number" value={formData.aadhar_number} onChange={handleInputChange} placeholder="12-digit Aadhar" className={errors.aadhar_number ? "input-error" : ""} />
                                            </div>
                                            <div className="field-group">
                                                <label>PAN Number <span className="required">*</span></label>
                                                <input type="text" name="pan_number" value={formData.pan_number} onChange={handleInputChange} placeholder="e.g. ABCDE1234F" className={errors.pan_number ? "input-error" : ""} />
                                            </div>
                                            <div className="field-group">
                                                <label>Passport Number</label>
                                                <input type="text" name="passport_number" value={formData.passport_number} onChange={handleInputChange} placeholder="Enter passport" className={errors.passport_number ? "input-error" : ""} />
                                            </div>
                                            <div className="field-group">
                                                <label>Passport Expiry</label>
                                                <input type="date" name="passport_expiry" value={formData.passport_expiry} onClick={e => e.target.showPicker?.()} onChange={handleInputChange} className={errors.passport_expiry ? "input-error" : ""} />
                                            </div>
                                            <div className="field-group">
                                                <label>Passport Country</label>
                                                <input type="text" name="passport_country" value={formData.passport_country} onChange={handleInputChange} placeholder="e.g. India" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ══ SECTION 4: Emergency Contacts ══ */}
                            <SectionHeader id="ec" title="🚨 Emergency Contacts" openSections={openSections} toggleSection={toggleSection} />
                            {openSections.has("ec") && (
                                <div style={{ padding: "10px 0", display: "flex", flexDirection: "column", gap: 20 }}>
                                    {/* Always show EC1 - mandatory (core 4 fields: First Name, Last Name, Relation, Phone) */}
                                    <EmergencyContactBlock label="Emergency Contact 1" ecKey="ec1" formData={formData} setFormData={setFormData} required errors={errors} />

                                    {/* Show EC2 only if ecCount >= 2 - fully optional */}
                                    {ecCount >= 2 && (
                                        <EmergencyContactBlock label="Emergency Contact 2" ecKey="ec2" formData={formData} setFormData={setFormData} errors={errors} />
                                    )}

                                    {/* Show EC3 only if ecCount >= 3 - fully optional */}
                                    {ecCount >= 3 && (
                                        <EmergencyContactBlock label="Emergency Contact 3" ecKey="ec3" formData={formData} setFormData={setFormData} errors={errors} />
                                    )}

                                    {/* Add / Remove buttons */}
                                    <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                                        {ecCount < 3 && (
                                            <button
                                                type="button"
                                                className="save-btn"
                                                style={{ padding: "6px 16px", fontSize: 13 }}
                                                onClick={() => setEcCount(c => c + 1)}
                                            >
                                                + Add Emergency Contact
                                            </button>
                                        )}
                                        {ecCount > 1 && (
                                            <button
                                                type="button"
                                                className="cancel-btn"
                                                style={{ padding: "6px 16px", fontSize: 13 }}
                                                onClick={handleRemoveLastEc}
                                            >
                                                − Remove Last
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* ── Actions ── */}
                            {/* ══ SECTION 5: Notes & Resume ══ */}
                            <SectionHeader id="notes" title="📝 Notes & Resume" openSections={openSections} toggleSection={toggleSection} />
                            {openSections.has("notes") && (
                                <div className="modal-fields">
                                    <div className="field-group" style={{ flex: "1 1 100%" }}>
                                        <label>Internal Notes</label>
                                        <textarea
                                            name="internal_notes"
                                            value={formData.internal_notes}
                                            onChange={handleInputChange}
                                            placeholder="Add any internal notes about this employee..."
                                            rows={4}
                                            style={{ width: "100%", resize: "vertical", padding: "8px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 }}
                                        />
                                    </div>
                                    <div className="field-group" style={{ flex: "1 1 100%" }}>
                                        <label>Resume</label>
                                        {editingEmp?.resume_original_name && !resumeFile && !resumePendingDelete && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "6px 10px", background: "#f0f8ff", borderRadius: 6, border: "1px solid #cce0ff" }}>
                                                <i className="fa-solid fa-file-pdf" style={{ color: "#e74c3c" }} />
                                                <span style={{ fontSize: 13, flex: 1 }}>{editingEmp.resume_original_name}</span>
                                                <span
                                                    title="Remove resume"
                                                    style={{ cursor: "pointer", color: "#e74c3c", fontWeight: 700, fontSize: 16 }}
                                                    onClick={async () => {
                                                        const ok = await dnAlert.confirm({
                                                            title: "Remove Resume?",
                                                            danger: true,
                                                            confirmLabel: "Yes, remove",
                                                        });
                                                        if (ok) {
                                                            // Stage the delete - actual API call happens on Save
                                                            setResumePendingDelete(true);
                                                        }
                                                    }}
                                                >✕</span>
                                            </div>
                                        )}
                                        {resumePendingDelete && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "6px 10px", background: "#fff3f3", borderRadius: 6, border: "1px solid #f5c6cb" }}>
                                                <span style={{ fontSize: 13, color: "#e74c3c" }}>⚠️ Resume will be removed on Save</span>
                                                <span
                                                    title="Undo"
                                                    style={{ cursor: "pointer", color: "#28a745", fontWeight: 700, fontSize: 13, marginLeft: "auto" }}
                                                    onClick={() => setResumePendingDelete(false)}
                                                >↩ Undo</span>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx"
                                            onChange={e => setResumeFile(e.target.files[0] || null)}
                                            style={{ marginTop: 4 }}
                                        />
                                        <span style={{ fontSize: 11, color: "#888", marginTop: 4, display: "block" }}>Accepted: PDF, DOC, DOCX - Max 10MB</span>
                                        {resumeUploading && <span style={{ fontSize: 12, color: "#007bff" }}>⏳ Uploading resume...</span>}
                                    </div>
                                </div>
                            )}

                            {/* ── Actions ── */}
                            <div className="modal-actions">
                                <button className="save-btn" onClick={handleSave}>Save</button>
                                <button className="cancel-btn" onClick={() => { setShowModal(false); setResumeFile(null); setResumePendingDelete(false); }}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Employee_Center;